// App.jsx — Composant racine, v2.0.
// Nouveautés : groupes 100 % côté client (fan-out chiffré 1-à-1, le serveur ne
// voit que des messages pairwise ordinaires), enveloppes versionnées, vidéos
// 100 Mo par morceaux, "Annuler l'envoi" serveur, indicateur d'écriture,
// accusés de lecture désactivables (transportés en enveloppe chiffrée : le
// serveur ne peut pas les distinguer d'un message), TOFU sur changement de
// clé, mode sombre, compression d'image, PWA.
import React, { useEffect, useRef, useState } from 'react';
import {
  initSodium, decryptFrom, encryptFor, encryptBytesFor, decryptBytesFrom,
  encryptChunk, decryptChunkedFile, classifyFile, fingerprint,
  generatePassphrase, deriveKeyPairFromPassphrase,
  encryptHistory, decryptHistory, encryptVaultBackup, decryptVaultBackup, safetyNumber,
  CHUNK_SIZE, VIDEO_MAX, FILE_MAX,
} from './crypto.js';
import { maybeCompressImage } from './compression.js';
import * as store from './storage.js';
import { WsClient } from './ws.js';
import { beep } from './notifications.js';
import { subscribePush, unsubscribePush } from './push.js';
import { parseQrPayload } from './qr.js';
import { normalizeBlocked } from './safe-data.js';
import { mergeShoppingItems, normalizeShoppingTextItems, normalizeUiStyle } from './collaboration-utils.js';
import { computeExpiresAt, normalizeConversationKeys, normalizeEphemeralSeconds, hasLogicalDuplicate, messagesMatchingRemoteDeletion } from './messaging-tools.js';
import { isAppLockConfigured, setAppLockCode, verifyAppLockCode, disableAppLock, rememberHiddenAt, hiddenDuration, clearHiddenAt } from './app-lock.js';
import { sealLocalString, openLocalString } from './local-vault.js';
import { platformAuthenticatorAvailable, registerVaultBiometric, unlockVaultBiometric } from './webauthn-vault.js';
import { AudioCallSession, supportsAudioCalls, supportsVideoCalls } from './audio-call.js';
import { primeCallAudio, stopCallTone } from './call-tones.js';
import { appendCallHistory, clearCallHistory, migrateCallHistoryEncryption } from './call-history.js';
import { makeCallSignal, normalizeCallSignal, signalTargetsDevice, shouldAcknowledgeCallSignal, candidateComesFromSelectedDevice, candidatesForSelectedDevice, isTerminalCallAction, DEVICE_ID_RE } from './call-signals.js';
import Login from './components/Login.jsx';
import ConversationList from './components/ConversationList.jsx';
import Chat from './components/Chat.jsx';
import AudioCallOverlay from './components/AudioCallOverlay.jsx';

// Détection app installée sur écran d'accueil (iOS Safari : navigator.standalone ;
// autres navigateurs : display-mode standalone). Purement informatif côté UI,
// aucune donnée envoyée nulle part.
function isStandalonePwa() {
  return window.navigator.standalone === true
    || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

// --- Enveloppes chiffrées versionnées (v2) ---
// Tout le "routage applicatif" (groupe, accusé de lecture) voyage À L'INTÉRIEUR
// du chiffrement : le serveur ne voit que des messages 1-à-1 indistinguables.
function makeEnvelope(kind, fields) { return JSON.stringify({ v: 2, kind, ...fields }); }
function parseEnvelope(plaintext) {
  try {
    const o = JSON.parse(plaintext);
    if (o && o.v === 2 && o.kind) return o;
  } catch (_) { /* message d'un client v1 : texte brut */ }
  return { v: 1, kind: 'text', text: plaintext };
}

// v3.0 : fin de la fenêtre de sécurisation des comptes créés avant la
// passphrase obligatoire. Après cette date, un compte hérité doit être
// sécurisé pour continuer (les comptes abandonnés tombent de toute façon
// sous la purge d'inactivité 7 jours déjà en place — aucune logique serveur).
const TRANSITION_DEADLINE = new Date('2026-09-01T23:59:59Z').getTime();

function LocalLockScreen({ onUnlock }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setError('');
    try { if (await onUnlock(code)) setCode(''); else setError('Code incorrect.'); }
    catch (_) { setError('Déverrouillage impossible.'); }
  };
  return <div className="center app-lock-screen"><form className="card app-lock-card" onSubmit={submit}>
    <div className="app-lock-icon">🔐</div><h1>Application verrouillée</h1>
    <p className="small muted">Le verrou masque l’interface. Le coffre v4.9 chiffre également les données persistées dans IndexedDB.</p>
    <input autoFocus type="password" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code local" />
    {error && <p className="error">{error}</p>}
    <button>Déverrouiller</button>
  </form></div>;
}


function LocalVaultScreen({ mode, legacyRows = 0, error = '', biometricConfigured = false, biometricAvailable = false, onSetup, onSetupAutomatic, onUnlock, onBiometricUnlock }) {
  const [code, setCode] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [backupConfirmed, setBackupConfirmed] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const setup = mode === 'setup';
  async function submit(e) {
    e.preventDefault(); setLocalError('');
    if (code.length < 8) return setLocalError('Code du coffre : 8 caractères minimum.');
    if (setup && code !== confirm) return setLocalError('Les deux codes ne correspondent pas.');
    if (setup && legacyRows > 0 && !backupConfirmed) return setLocalError('Confirmez d’abord avoir exporté identité + historique depuis la version précédente.');
    setBusy(true);
    try { if (setup) await onSetup(code); else await onUnlock(code); setCode(''); setConfirm(''); }
    catch (err) { setLocalError(err.message || 'Impossible d’ouvrir le coffre local.'); }
    finally { setBusy(false); }
  }
  async function setupAutomatic() {
    setLocalError('');
    if (legacyRows > 0 && !backupConfirmed) return setLocalError('Confirmez d’abord avoir exporté identité + historique depuis la version précédente.');
    setBusy(true);
    try { await onSetupAutomatic(); setCode(''); setConfirm(''); }
    catch (err) { setLocalError(err.message || 'Impossible d’activer le mode pratique.'); }
    finally { setBusy(false); }
  }
  return <div className="center vault-screen"><form className="card vault-card" onSubmit={submit}>
    <div className="app-lock-icon">🔐</div>
    <h1>{setup ? 'Protection du stockage local' : 'Déverrouiller le coffre'}</h1>
    {setup ? <>
      <p className="small">La v4.9 chiffre maintenant <strong>identité, clés privées, contacts, messages, médias, groupes et réglages</strong> avant leur stockage dans IndexedDB.</p>
      {legacyRows > 0 && <p className="notice warn small">Migration locale détectée : {legacyRows} entrée{legacyRows > 1 ? 's' : ''} existante{legacyRows > 1 ? 's' : ''}. L’ancien stockage n’est effacé qu’au sein de la même transaction qui écrit sa copie chiffrée.</p>}
      {legacyRows > 0 && <label className="row vault-backup-confirm"><input type="checkbox" checked={backupConfirmed} onChange={(e) => setBackupConfirmed(e.target.checked)} /><span className="small">J’ai exporté mon identité et mon historique avant cette migration.</span></label>}
      <p className="small muted">Protection renforcée : le code ne quitte jamais cet appareil. S’il est perdu, le coffre local ne peut pas être récupéré sans un export d’identité/historique.</p>
    </> : <p className="small muted">La clé maître est déchiffrée uniquement en mémoire vive pour cette session.</p>}
    <input autoFocus type="password" autoComplete="current-password" value={code} onChange={(e) => setCode(e.target.value)} placeholder={setup ? 'Créer un code pour le mode renforcé' : 'Code du coffre'} />
    {setup && <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmer le code" />}
    {(localError || error) && <p className="error">{localError || error}</p>}
    <button disabled={busy || bioBusy}>{busy ? (setup ? 'Chiffrement en cours…' : 'Ouverture…') : (setup ? 'Activer le mode renforcé' : 'Déverrouiller')}</button>
    {setup && <button type="button" className="ghost" disabled={busy || bioBusy} onClick={setupAutomatic}>Continuer sans déverrouillage à chaque ouverture (mode pratique)</button>}
    {setup && <p className="small muted">Le mode pratique conserve les données chiffrées dans IndexedDB mais stocke sur cet appareil une clé d’ouverture automatique. Il est plus confortable mais protège moins contre l’extraction complète du profil navigateur.</p>}
    {!setup && biometricConfigured && biometricAvailable && <button type="button" className="ghost vault-biometric-button" disabled={busy || bioBusy} onClick={async () => {
      setLocalError(''); setBioBusy(true);
      try { await onBiometricUnlock(); }
      catch (err) { setLocalError(err.message || 'Déverrouillage biométrique impossible.'); }
      finally { setBioBusy(false); }
    }}>{bioBusy ? 'Authentification…' : '🔐 Déverrouiller avec la biométrie / le système'}</button>}
    {!setup && biometricConfigured && !biometricAvailable && <p className="small muted">La biométrie WebAuthn configurée n’est pas disponible dans ce navigateur ; le code du coffre reste utilisable.</p>}
    <p className="small muted">Argon2id (paramètres adaptés mobile) + clé maître aléatoire + secretbox authentifié. Aucun secret n’est envoyé au serveur.</p>
  </form></div>;
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [vaultPhase, setVaultPhase] = useState('checking'); // checking | setup | locked | unlocked
  const [vaultLegacyRows, setVaultLegacyRows] = useState(0);
  const [vaultError, setVaultError] = useState('');
  const [vaultWebAuthnConfig, setVaultWebAuthnConfig] = useState(null);
  const [vaultBiometricAvailable, setVaultBiometricAvailable] = useState(false);
  const [vaultLockPolicy, setVaultLockPolicyState] = useState('launch');
  const [vaultAutomatic, setVaultAutomatic] = useState(false);
  const vaultHiddenAtRef = useRef(0);
  const [identity, setIdentity] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [convMeta, setConvMeta] = useState({});
  const [activePeer, setActivePeer] = useState(null); // pseudo OU groupId
  const [messages, setMessages] = useState([]);
  const [presence, setPresence] = useState({});
  const [typing, setTyping] = useState({});           // pseudo → true (éphémère)
  const [status, setStatus] = useState('disconnected');
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [receiptsEnabled, setReceiptsEnabled] = useState(false); // OFF par défaut (privacy)
  const [darkMode, setDarkMode] = useState(false);
  const [fontScale, setFontScale] = useState('md');       // v4.1 : 'sm' | 'md' | 'lg' | 'xl'
  const [highContrast, setHighContrast] = useState(false); // v4.1 : accessibilité
  const [blocked, setBlocked] = useState([]);              // v4.1 : pseudos bloqués (local uniquement)
  const [exportReminder, setExportReminder] = useState(false); // v4.1 : rappel d'export périodique
  const [keyAlert, setKeyAlert] = useState(null);     // { pseudo, oldFp, newFp, newKey }
  const [banner, setBanner] = useState('');
  const [securing, setSecuring] = useState(null);
  const [storagePersisted, setStoragePersisted] = useState(true); // v4 : alerte si refusé
  const [sendQueue, setSendQueue] = useState(0);                   // v4 : file d'envoi globale
  const [replyTo, setReplyTo] = useState(null);                    // v4 : message référencé
  const [firstRunNotice, setFirstRunNotice] = useState(false);
  const [shoppingItems, setShoppingItems] = useState([]);
  const [bubbleColor, setBubbleColor] = useState('blue');
  const [uiStyle, setUiStyle] = useState('classic');
  const [pinnedConversations, setPinnedConversations] = useState([]);
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [focusMessageId, setFocusMessageId] = useState(null);
  const [lastExportAt, setLastExportAt] = useState(0);
  const [viewOnceConversations, setViewOnceConversations] = useState([]);
  const [remoteDeletionIds, setRemoteDeletionIds] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPreview, setAdminPreview] = useState(null);
  const [adminResult, setAdminResult] = useState(null);
  const [adminError, setAdminError] = useState('');
  const [appLockConfigured, setAppLockConfiguredState] = useState(() => isAppLockConfigured());
  const [appLocked, setAppLocked] = useState(() => isAppLockConfigured());
  const [rtcInfo, setRtcInfo] = useState({ enabled: false, productionReady: false, loading: true, reason: 'Initialisation des appels audio…', iceServers: [] });
  const [call, setCall] = useState(null);
  const [voiceMessageRequest, setVoiceMessageRequest] = useState(null); // v4.9.3 : vocal proposé après appel sans réponse
  const wsRef = useRef(null);
  const activePeerRef = useRef(null);
  const idMapRef = useRef({ byTmp: new Map(), byServer: new Map() });
  const settingsRef = useRef({ notif: false, receipts: false });
  const blockedRef = useRef([]); // v4.1 : lu de façon synchrone dans handleIncoming
  // v4.3 : ids serveur déjà traités — le serveur RETRANSMET désormais les
  // messages en attente (fiabilité), le client doit donc dédupliquer. Amorcé
  // depuis l'historique local (refreshAll) pour couvrir les redémarrages.
  const seenServerIdsRef = useRef(new Set());
  const processingServerIdsRef = useRef(new Set()); // v4.8 : ferme la race entre deux livraisons concurrentes du même id
  const messageFailureCountsRef = useRef(new Map()); // id serveur → échecs de déchiffrement consécutifs
  // v4.3.1 : réception fiable — dernier signalement d'échec par contact
  // (1/min max), cache des clés vérifiées à l'envoi (5 min), et canal de
  // synchronisation entre instances (onglet Safari + PWA écran d'accueil).
  const failNoticeRef = useRef(new Map());
  const keyCheckRef = useRef(new Map());
  const syncChRef = useRef(null);
  const typingTimers = useRef({});
  const identityRef = useRef(null);
  const securingRef = useRef(null);
  const deviceIdRef = useRef('');
  const callRef = useRef(null);
  const callSessionRef = useRef(null);
  const callTimersRef = useRef(new Set());
  const pendingCallCandidatesRef = useRef(new Map());
  const seenCallSignalIdsRef = useRef(new Set());
  const completedCallIdsRef = useRef(new Set()); // borné : bloque les offres rejouées/annulées
  const loggedCallIdsRef = useRef(new Set()); // historique d'appels local : une seule entrée par callId
  const archivedConversationsRef = useRef([]);
  const remoteDeletionIdsRef = useRef([]);
  activePeerRef.current = activePeer;
  settingsRef.current = { notif: notifEnabled, receipts: receiptsEnabled };
  blockedRef.current = blocked;
  identityRef.current = identity;
  securingRef.current = securing;
  callRef.current = call;
  archivedConversationsRef.current = archivedConversations;
  remoteDeletionIdsRef.current = remoteDeletionIds;

  useEffect(() => {
    document.documentElement.dataset.bubbleColor = bubbleColor || 'blue';
  }, [bubbleColor]);

  // v4.9.3 : amorce Web Audio au premier geste utilisateur. Safari/iOS
  // autorise ensuite la sonnerie locale même si le signal `ringing` arrive
  // quelques secondes après le clic initial.
  useEffect(() => {
    const prime = () => { primeCallAudio().catch(() => {}); };
    window.addEventListener('pointerdown', prime, { capture: true, once: true });
    window.addEventListener('keydown', prime, { capture: true, once: true });
    return () => {
      window.removeEventListener('pointerdown', prime, { capture: true });
      window.removeEventListener('keydown', prime, { capture: true });
    };
  }, []);

  useEffect(() => {
    document.documentElement.dataset.uiStyle = normalizeUiStyle(uiStyle);
  }, [uiStyle]);

  // v4.7.0 : verrou UI local. Au retour après 30 s en arrière-plan,
  // l'interface est masquée jusqu'au code local. La réception E2E peut continuer.
  useEffect(() => {
    if (!appLockConfigured || vaultPhase === 'unlocked') return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') rememberHiddenAt(Date.now());
      else if (hiddenDuration(Date.now()) >= 30000) setAppLocked(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [appLockConfigured, vaultPhase]);

  // v4.9.1 : le coffre peut rester ouvert lors d'un simple passage en arrière-plan
  // (politique « ouverture uniquement ») ou se refermer après 30 s. Une fermeture /
  // destruction réelle de la PWA perd de toute façon la DEK en mémoire et impose
  // un nouveau déverrouillage au prochain lancement.
  useEffect(() => {
    if (vaultPhase !== 'unlocked') return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') vaultHiddenAtRef.current = Date.now();
      else if (vaultLockPolicy === 'background30' && vaultHiddenAtRef.current && Date.now() - vaultHiddenAtRef.current >= 30000) hardLockVault();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [vaultPhase, vaultLockPolicy]);

  async function configureLocalLock(code) {
    await setAppLockCode(code);
    setAppLockConfiguredState(true); setAppLocked(false); clearHiddenAt();
    return true;
  }
  async function unlockLocalApp(code) {
    const ok = await verifyAppLockCode(code);
    if (ok) { clearHiddenAt(); setAppLocked(false); }
    return ok;
  }
  function disableLocalLock() {
    disableAppLock(); setAppLockConfiguredState(false); setAppLocked(false); clearHiddenAt();
  }
  function lockLocalAppNow() { if (appLockConfigured) setAppLocked(true); }

  // v4.6.0 : les messages éphémères sont purgés UNIQUEMENT localement après
  // l'expiration choisie par l'expéditeur. Aucun ancien message sans expiresAt
  // n'est concerné, et aucune information de purge n'est envoyée au serveur.
  useEffect(() => {
    if (!identity) return undefined;
    let cancelled = false;
    const purge = async () => {
      try {
        const removed = await store.deleteExpiredMessages(Date.now());
        if (!cancelled && removed) {
          await refreshAll();
          if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
        }
      } catch (_) { /* maintenance locale non bloquante */ }
    };
    purge();
    const timer = setInterval(purge, 30000);
    const onVisible = () => { if (document.visibilityState === 'visible') purge(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [identity]);

  async function loadLocalStateAfterVault() {
    // v4.9.1 : le coffre remplace le verrou UI historique pendant une session ouverte.
    setAppLocked(false); clearHiddenAt();
    migrateCallHistoryEncryption();
    for (const [key, scope] of [['messagerie:last-audio-network:v1', 'audio-network'], ['messagerie:last-audio-quality:v1', 'audio-quality']]) {
      try { const raw = localStorage.getItem(key); if (raw) { const plain = openLocalString(scope, raw); localStorage.setItem(key, sealLocalString(scope, plain)); } } catch (_) {}
    }
    const id = await store.getIdentity();
    setNotifEnabled(await store.getSetting('notifications', false));
    setReceiptsEnabled(await store.getSetting('readReceipts', false));
    const dark = await store.getSetting('darkMode', window.matchMedia('(prefers-color-scheme: dark)').matches);
    setDarkMode(dark);
    setFontScale(await store.getSetting('fontScale', 'md'));
    setHighContrast(await store.getSetting('highContrast', false));
    setBlocked(normalizeBlocked(await store.getSetting('blockedPseudos', [])));
    const savedShopping = await store.getSetting('shoppingItems', []);
    setShoppingItems(Array.isArray(savedShopping) ? savedShopping.filter((x) => x && typeof x.text === 'string') : []);
    const savedExportAt = await store.getSetting('lastExportAt', 0);
    setLastExportAt(savedExportAt);
    setExportReminder(!savedExportAt || (Date.now() - savedExportAt) > 14 * 86400000);
    setBubbleColor(await store.getSetting('bubbleColor', 'blue'));
    setUiStyle(normalizeUiStyle(await store.getSetting('uiStyle', 'classic')));
    setPinnedConversations(normalizeConversationKeys(await store.getSetting('pinnedConversations', [])));
    setArchivedConversations(normalizeConversationKeys(await store.getSetting('archivedConversations', [])));
    setViewOnceConversations(normalizeConversationKeys(await store.getSetting('viewOnceConversations', [])));
    const savedVaultLockPolicy = await store.getSetting('vaultLockPolicy', 'launch');
    setVaultLockPolicyState(['launch', 'background30'].includes(savedVaultLockPolicy) ? savedVaultLockPolicy : 'launch');
    setVaultWebAuthnConfig(await store.getVaultWebAuthnConfig());
    const savedDeletionIds = normalizeConversationKeys(await store.getSetting('remoteDeletionIds', []));
    setRemoteDeletionIds(savedDeletionIds); remoteDeletionIdsRef.current = savedDeletionIds;
    let deviceId = await store.getSetting('deviceId', '');
    if (!DEVICE_ID_RE.test(String(deviceId || ''))) {
      deviceId = `dev_${crypto.randomUUID().replace(/-/g, '')}`;
      await store.setSetting('deviceId', deviceId);
    }
    deviceIdRef.current = deviceId;
    if (id) await startSession(id);
    setReady(true);
  }

  async function setupLocalVault(code) {
    setVaultError('');
    await store.initializeVault(code);
    setVaultPhase('unlocked');
    await loadLocalStateAfterVault();
  }
  async function setupAutomaticVault() {
    setVaultError('');
    await store.initializeAutomaticVault();
    setVaultAutomatic(true);
    setVaultLockPolicyState('launch');
    setVaultPhase('unlocked');
    await loadLocalStateAfterVault();
  }
  async function unlockLocalVault(code) {
    setVaultError('');
    await store.unlockVault(code);
    setVaultPhase('unlocked');
    await loadLocalStateAfterVault();
  }
  async function changeLocalVaultCode(oldCode, newCode) {
    await store.changeVaultCode(oldCode, newCode);
    return true;
  }
  async function enableVaultBiometric() {
    if (!(await platformAuthenticatorAvailable())) throw new Error('Authentification biométrique / système WebAuthn indisponible sur cet appareil ou ce navigateur.');
    const created = await registerVaultBiometric(identityRef.current?.pseudo || identity?.pseudo || 'utilisateur');
    try {
      await store.enableVaultWebAuthn({ credentialId: created.credentialId, prfSalt: created.prfSalt, createdAt: created.createdAt }, created.prfSecret);
    } finally { try { created.prfSecret?.fill?.(0); } catch (_) {} }
    const cfg = await store.getVaultWebAuthnConfig(); setVaultWebAuthnConfig(cfg); return true;
  }
  async function disableVaultBiometric() {
    await store.disableVaultWebAuthn(); setVaultWebAuthnConfig(null); return true;
  }
  async function unlockLocalVaultBiometric() {
    const cfg = vaultWebAuthnConfig || await store.getVaultWebAuthnConfig();
    if (!cfg) throw new Error('Déverrouillage biométrique non configuré.');
    const secret = await unlockVaultBiometric(cfg);
    try { await store.unlockVaultWithWebAuthn(secret); }
    finally { try { secret?.fill?.(0); } catch (_) {} }
    setVaultPhase('unlocked'); await loadLocalStateAfterVault();
  }
  async function setVaultLockPolicy(value) {
    const next = value === 'background30' ? 'background30' : 'launch';
    await store.setSetting('vaultLockPolicy', next); setVaultLockPolicyState(next); return next;
  }
  async function enableVaultAutomaticMode() {
    await store.enableVaultAutomaticUnlock();
    await store.setSetting('vaultLockPolicy', 'launch');
    setVaultLockPolicyState('launch'); setVaultAutomatic(true); return true;
  }
  async function disableVaultAutomaticMode(newCode) {
    if (String(newCode || '').length < 8) throw new Error('Nouveau code du coffre : 8 caractères minimum.');
    await store.disableVaultAutomaticUnlock(newCode);
    setVaultAutomatic(false); return true;
  }
  function hardLockVault() {
    try { store.lockVault(); } catch (_) {}
    try { wsRef.current?.close(); } catch (_) {}
    try { callSessionRef.current?.close(false).catch(() => {}); } catch (_) {}
    setIdentity(null); setContacts([]); setGroups([]); setConvMeta({}); setMessages([]); setActivePeer(null); setReady(false); setVaultPhase('locked');
  }
  async function exportFullVaultBackup(passphrase) {
    const payload = await store.collectFullVaultBackup();
    const blob = new Blob([encryptVaultBackup(payload, passphrase)], { type: 'application/json' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = `sauvegarde-coffre-${payload.identity.pseudo}-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    const exportedAt = Date.now(); await store.setSetting('lastExportAt', exportedAt); setLastExportAt(exportedAt); setExportReminder(false);
  }
  async function importFullVaultBackup(file, passphrase) {
    const payload = decryptVaultBackup(await file.text(), passphrase);
    await store.restoreFullVaultBackup(payload);
    await store.deleteExpiredMessages(Date.now());
    try { wsRef.current?.close(); } catch (_) {}
    window.location.reload();
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await initSodium();
        const status = await store.getVaultStatus();
        if (cancelled) return;
        setVaultLegacyRows(Number(status.legacyPlaintext || 0));
        setVaultWebAuthnConfig(await store.getVaultWebAuthnConfig());
        setVaultBiometricAvailable(await platformAuthenticatorAvailable().catch(() => false));
        setVaultAutomatic(!!status.autoUnlock);
        if (status.configured && status.autoUnlock) {
          try {
            await store.unlockVaultAutomatically();
            if (cancelled) return;
            setVaultPhase('unlocked');
            await loadLocalStateAfterVault();
          } catch (autoError) {
            if (cancelled) return;
            setVaultError(`Ouverture automatique impossible : ${autoError.message || 'erreur'}. Utilisez le code du coffre si disponible.`);
            setVaultPhase('locked');
          }
        } else {
          setVaultPhase(status.configured ? 'locked' : 'setup');
        }
      } catch (error) {
        if (!cancelled) { setVaultError(error.message || 'Initialisation du coffre impossible.'); setVaultPhase('setup'); }
      }
    })();
    return () => { cancelled = true; wsRef.current?.close(); callSessionRef.current?.close(false).catch(() => {}); };
  }, []);

  // v4.3.1 : SYNCHRONISATION ENTRE INSTANCES. Si le site est ouvert à la fois
  // dans un onglet Safari ET en PWA installée (cas réel fréquent sur iOS), le
  // serveur n'accepte qu'UNE connexion par pseudo : les deux instances se la
  // volent en boucle, et celle que la personne regarde peut ne JAMAIS recevoir
  // — alors que l'autre a bien stocké les messages dans l'IndexedDB partagée.
  // Symptôme exact : « envoyé ✓ mais rien n'arrive ». Remède : l'instance qui
  // stocke prévient les autres (BroadcastChannel + repli localStorage), qui
  // relisent la base locale. Aucune donnée ne transite : simple « toc toc ».
  useEffect(() => {
    if (vaultPhase !== 'unlocked') return undefined;
    const apply = async () => {
      await refreshAll();
      if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
    };
    let ch = null;
    if (typeof BroadcastChannel !== 'undefined') {
      ch = new BroadcastChannel('msgring-sync');
      ch.onmessage = () => { apply(); };
      syncChRef.current = ch;
    }
    const onStorage = (e) => { if (e.key === 'msgring-ping') apply(); };
    window.addEventListener('storage', onStorage);
    return () => { try { ch?.close(); } catch (_) {} window.removeEventListener('storage', onStorage); };
  }, [vaultPhase]);
  function pingOtherInstances() {
    try { syncChRef.current?.postMessage('refresh'); } catch (_) {}
    try { localStorage.setItem('msgring-ping', String(Date.now())); } catch (_) {}
  }

  // Mode sombre : attribut sur <html>, stylé en CSS pur.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // v4.1 : taille de police et contraste élevé — mêmes attributs data-*
  // stylés en CSS pur (accessibilité, aucun risque de sécurité/privacy).
  useEffect(() => {
    document.documentElement.setAttribute('data-font-scale', fontScale);
  }, [fontScale]);
  useEffect(() => {
    document.documentElement.setAttribute('data-contrast', highContrast ? 'high' : 'normal');
  }, [highContrast]);

  const isGroupId = (p) => typeof p === 'string' && p.startsWith('grp:');
  const groupOf = (p) => groups.find((g) => g.groupId === p);

  async function setConversationPinned(key, enabled) {
    const clean = String(key || '').trim(); if (!clean || clean === identityRef.current?.pseudo) return;
    const current = normalizeConversationKeys(await store.getSetting('pinnedConversations', []));
    const next = enabled ? normalizeConversationKeys([...current, clean]) : current.filter((x) => x !== clean);
    await store.setSetting('pinnedConversations', next); setPinnedConversations(next);
  }

  async function setConversationArchived(key, enabled) {
    const clean = String(key || '').trim(); if (!clean || clean === identityRef.current?.pseudo) return;
    const current = normalizeConversationKeys(await store.getSetting('archivedConversations', []));
    const next = enabled ? normalizeConversationKeys([...current, clean]) : current.filter((x) => x !== clean);
    await store.setSetting('archivedConversations', next); setArchivedConversations(next); archivedConversationsRef.current = next;
  }

  async function unarchiveOnIncoming(key) {
    if (!archivedConversationsRef.current.includes(key)) return;
    await setConversationArchived(key, false);
  }

  async function setConversationViewOnce(key, enabled) {
    const clean = String(key || '').trim(); if (!clean) return;
    const current = normalizeConversationKeys(await store.getSetting('viewOnceConversations', []));
    const next = enabled ? normalizeConversationKeys([...current, clean]) : current.filter((x) => x !== clean);
    await store.setSetting('viewOnceConversations', next); setViewOnceConversations(next);
  }

  async function rememberRemoteDeletion(targetId) {
    const id = String(targetId || '').trim(); if (!id) return;
    const current = normalizeConversationKeys(remoteDeletionIdsRef.current);
    const next = normalizeConversationKeys([...current.filter((x) => x !== id), id]).slice(-500);
    remoteDeletionIdsRef.current = next; setRemoteDeletionIds(next);
    await store.setSetting('remoteDeletionIds', next);
  }

  async function refreshAll() {
    // v4.3.7 : les getters IndexedDB normalisent désormais contacts, groupes
    // et messages. On garde en plus un dictionnaire sans prototype afin qu'un
    // pseudo spécial (ex. « __proto__ ») ne puisse jamais détourner les accès
    // aux métadonnées de conversations.
    setContacts(await store.getContacts());
    setGroups(await store.getGroups());
    const all = await store.getAllMessageMetadata();
    const meta = Object.create(null);
    for (const m of all) {
      if (Number(m.expiresAt || 0) > 0 && Number(m.expiresAt) <= Date.now()) continue;
      if (m.serverId) seenServerIdsRef.current.add(m.serverId); // v4.3 : amorce dédup
      const e = meta[m.peer] || (meta[m.peer] = { last: null, unread: 0 });
      if (!e.last || m.sentAt >= e.last.sentAt) e.last = m;
      if (!m.read) e.unread++;
    }
    setConvMeta(meta);
  }

  async function startSession(id) {
    setIdentity(id);
    // v3.0 : demande de stockage PERSISTANT — réduit fortement le risque que
    // le navigateur purge IndexedDB (cause première des comptes « oubliés »).
    try {
      const persisted = navigator.storage?.persist ? await navigator.storage.persist() : true;
      setStoragePersisted(persisted !== false);
    } catch (_) { setStoragePersisted(true); }
    await refreshAll();
    const ws = new WsClient(id, {
      onStatus: (next) => {
        setStatus(next);
        if (next !== 'connected') setIsAdmin(false);
        if (next === 'connected') loadRtcConfig(ws).catch(() => {});
      },
      onPresence: (p) => setPresence((prev) => ({ ...prev, [p.pseudo]: p.online })),
      onAdminStatus: (value) => setIsAdmin(value === true),
      onAdminPreview: (data) => { setAdminError(''); setAdminResult(null); setAdminPreview(data); },
      onAdminResult: (data) => {
        setAdminError(''); setAdminPreview(null); setAdminResult(data);
        if (data?.action === 'panic-server') setBanner('☢️ PANIC serveur exécuté. Les données locales de cet appareil n’ont pas été effacées.');
        else setBanner('Action administrateur exécutée.');
      },
      onAdminError: (message) => { setAdminPreview(null); setAdminError(message || 'Action administrateur refusée.'); },
      onMessage: (m) => handleIncoming(id, m),
      onSent: async (d) => {
        const localId = idMapRef.current.byTmp.get(d.tmpId);
        if (localId === undefined) return;
        idMapRef.current.byTmp.delete(d.tmpId);
        idMapRef.current.byServer.set(d.id, localId);
        // v3.1 : mutation ATOMIQUE — les N confirmations d'un fan-out de groupe
        // arrivent en rafale ; le lire-modifier-écrire concurrent d'avant
        // perdait des incréments (⏳ éternel). Une transaction IDB unique
        // sérialise et rend le compteur exact.
        await store.mutateMessage(localId, (msg) => {
          const sentCount = (msg.sentCount || 0) + 1;
          const done = sentCount + (msg.failedCount || 0) >= (msg.total || 1);
          const out = {
            ...msg,
            serverIds: [...(msg.serverIds || []), d.id],
            sentCount,
            status: msg.status === 'delivered' || msg.status === 'read'
              ? msg.status
              : (done ? (msg.deliverAt && msg.deliverAt > Date.now() ? 'scheduled' : 'sent') : 'sending'),
          };
          // Une photo/vidéo « voir une fois » n'est jamais conservée comme
          // galerie locale côté expéditeur après confirmation serveur.
          if (done && out.viewOnce && ['image', 'video'].includes(out.msgType)) {
            out.data = undefined; out.blob = undefined;
          }
          return out;
        });
        if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
      },
      onDelivered: async (d) => {
        const localId = idMapRef.current.byServer.get(d.id);
        if (localId === undefined) return;
        await store.mutateMessage(localId, (msg) => {
          const deliveredCount = (msg.deliveredCount || 0) + 1;
          // Livré à tous les destinataires ATTEINTS (total - échecs définitifs).
          const reachable = (msg.total || 1) - (msg.failedCount || 0);
          const out = { ...msg, deliveredCount };
          if (deliveredCount >= reachable && msg.status !== 'read') out.status = 'delivered';
          return out;
        });
        if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
      },
      onUnsent: async (d) => {
        const localId = idMapRef.current.byServer.get(d.id);
        if (d.ok) {
          if (localId !== undefined) {
            await store.deleteMessage(localId);
            idMapRef.current.byServer.delete(d.id);
            if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
            await refreshAll();
          }
          setBanner('Envoi annulé — le serveur a supprimé le message avant livraison.');
        } else {
          setBanner('Annulation impossible : le message a déjà été livré (le serveur ne l’a plus).');
        }
        setTimeout(() => setBanner(''), 5000);
      },
      onKeyRotated: async (d) => {
        // Confirmation serveur de la rotation : on bascule sur la nouvelle
        // identité dérivée SEULEMENT maintenant (jamais avant l'ok serveur).
        if (d.ok && securingRef.current) {
          const { keys } = securingRef.current;
          const newId = { pseudo: identityRef.current.pseudo, ...keys, secured: true };
          await store.saveIdentity(newId);
          setBanner('Compte sécurisé — reconnexion avec votre nouvelle clé…');
          setTimeout(() => location.reload(), 1500); // reconnexion propre (nouveau challenge)
        } else if (!d.ok) {
          setBanner('Échec de la sécurisation : ' + (d.error || 'réessayez.'));
          setSecuring(null);
        }
      },
      onAccountDeleted: async () => {
        clearCallHistory();
        try { localStorage.removeItem('messagerie:last-audio-network:v1'); localStorage.removeItem('messagerie:last-audio-quality:v1'); } catch (_) {}
        await store.wipeAll();
        location.reload();
      },
      onCallSignal: (m) => handleCallSignal(id, m),
      onCallSignalError: (message) => { setBanner(message); setTimeout(() => setBanner(''), 5000); },
      onTyping: (d) => {
        // Éphémère : jamais stocké, disparaît après 4 s sans nouveau signal.
        setTyping((prev) => ({ ...prev, [d.from]: true }));
        clearTimeout(typingTimers.current[d.from]);
        typingTimers.current[d.from] = setTimeout(
          () => setTyping((prev) => ({ ...prev, [d.from]: false })), 4000);
      },
      onError: (e) => console.warn(e),
    });
    wsRef.current = ws;
    ws.connect();
  }

  async function ensureContact(pseudoName) {
    let contact = (await store.getContacts()).find((c) => c.pseudo === pseudoName);
    // v4.3.7 : une ancienne ligne peut avoir un pseudo valide mais une clé
    // absente/non textuelle. Elle ne doit ni faire planter libsodium, ni être
    // considérée comme un contact TOFU valide : on la rafraîchit auprès de
    // l'annuaire puis on réécrit localement la ligne saine.
    if (!contact || typeof contact.publicKey !== 'string' || !contact.publicKey) {
      const res = await fetch(`/api/users/${encodeURIComponent(pseudoName)}`);
      if (!res.ok) throw new Error('Pseudo introuvable.');
      const u = await res.json();
      if (typeof u.pseudo !== 'string' || typeof u.publicKey !== 'string' || !u.publicKey) {
        throw new Error('Réponse annuaire invalide.');
      }
      // TOFU : la clé mémorisée ICI fait foi pour toutes les comparaisons futures.
      contact = { pseudo: u.pseudo, publicKey: u.publicKey };
      await store.saveContact(contact);
    }
    return contact;
  }

  // v4.1 : ajout/vérification d'un contact via QR code scanné en personne —
  // remplace la comparaison manuelle d'un long fingerprint hexadécimal.
  // Aucun appel réseau : la clé vient directement du QR affiché par l'autre
  // personne, pas de l'annuaire serveur (donc pas de confiance à lui accorder
  // pour cette vérification précise — c'est même plus fort que le TOFU normal).
  async function addOrVerifyContactByQr(scannedPseudo, scannedKey) {
    const existing = (await store.getContacts()).find((c) => c.pseudo === scannedPseudo);
    if (!existing) {
      await store.saveContact({
        pseudo: scannedPseudo, publicKey: scannedKey,
        verifiedKey: scannedKey, verifiedAt: Date.now(), verifiedMethod: 'qr',
      });
      setBanner(`Contact « ${scannedPseudo} » ajouté et vérifié par QR code.`);
    } else if (existing.publicKey === scannedKey) {
      await store.saveContact({ ...existing, verifiedKey: scannedKey, verifiedAt: Date.now(), verifiedMethod: 'qr' });
      setBanner(`Clé de « ${scannedPseudo} » confirmée par QR code — contact vérifié.`);
    } else {
      setBanner(`⚠️ La clé scannée pour « ${scannedPseudo} » diffère de celle déjà connue localement — ne faites pas confiance sans vérifier davantage.`);
    }
    setTimeout(() => setBanner(''), 6000);
    await refreshAll();
    return scannedPseudo;
  }

  // v4.3.1 : ÉCHEC DE TRAITEMENT D'UN MESSAGE REÇU — jusqu'ici totalement
  // silencieux (console invisible sur mobile) : un déchiffrement impossible
  // (cause n°1 : la clé de l'expéditeur a changé, compte recréé) laissait le
  // message en attente serveur, retransmis dans le vide, SANS AUCUN SIGNE.
  // Désormais : diagnostic automatique contre l'annuaire ; si la clé a bien
  // changé → alerte TOFU (l'écran habituel « J'ai vérifié — accepter ») +
  // bannière explicite. Après acceptation, la retransmission serveur (≤ 45 s)
  // relivre le message, qui se déchiffre alors : AUTO-RÉPARATION complète,
  // sans rien céder sur le TOFU (jamais de nouvelle clé acceptée en douce).
  async function handleProcessingFailure(m, err, stage = '') {
    try {
      const from = m.from || '?';
      const stored = (await store.getContacts()).find((c) => c.pseudo === from);
      let rotated = false;
      try {
        const res = await fetch(`/api/users/${encodeURIComponent(from)}`);
        if (res.ok && stored) {
          const u = await res.json();
          if (u.publicKey !== stored.publicKey) {
            rotated = true;
            setKeyAlert((cur) => cur || {
              pseudo: from,
              oldFp: fingerprint(stored.publicKey),
              newFp: fingerprint(u.publicKey),
              newKey: u.publicKey,
            });
          }
        }
      } catch (_) { /* annuaire injoignable : on signale sans diagnostic */ }
      const decryptFailure = String(stage || '').startsWith('decrypt');
      if (!rotated && decryptFailure && m?.id) {
        const count = Number(messageFailureCountsRef.current.get(m.id) || 0) + 1;
        messageFailureCountsRef.current.set(m.id, count);
        // Après trois échecs identiques et sans rotation de clé de l'expéditeur,
        // le ciphertext est signalé au serveur par son id technique uniquement.
        // L'admin pourra le purger ; aucune clé, erreur détaillée ou donnée claire
        // n'est transmise.
        if (count <= 3) wsRef.current?.reportMessageFailure?.(m.id);
      }
      const now = Date.now();
      if (now - (failNoticeRef.current.get(from) || 0) < 60000) return; // 1/min/contact
      failNoticeRef.current.set(from, now);
      setBanner(rotated
        ? `⚠️ Des messages de « ${from} » sont en attente : sa clé a changé (compte recréé ?). Ouvrez sa conversation et validez la nouvelle clé — ils arriveront tout seuls ensuite.`
        : `⚠️ Un message de « ${from} » n'a pas pu être traité (${(err && err.message) || 'erreur'}) — nouvel essai automatique en cours.${decryptFailure && Number(messageFailureCountsRef.current.get(m?.id) || 0) >= 3 ? ' Il a été signalé comme indéchiffrable ; l’administrateur peut désormais purger ce ciphertext bloqué.' : ''}`);
    } catch (_) { /* le diagnostic ne doit jamais casser la réception */ }
  }

  // --- Réception ---
  async function handleIncoming(id, m) {
    let processingStage = 'preflight';
    // Deux trames identiques peuvent arriver pendant qu'un gros média est encore
    // en cours de téléchargement/déchiffrement. `seenServerIds` n'est rempli
    // qu'après succès ; ce second set bloque la fenêtre de course sans ACKer
    // prématurément (le premier traitement doit pouvoir échouer et être rejoué).
    if (m?.id && processingServerIdsRef.current.has(m.id)) return;
    if (m?.id) processingServerIdsRef.current.add(m.id);
    try {
      // v4.3 : DÉDUPLICATION — le serveur retransmet les messages en attente
      // (fiabilité de livraison). Un id déjà traité est simplement ré-acquitté
      // (c'est que l'ack initial s'était perdu) : la file serveur se vide,
      // rien n'est réaffiché ni re-stocké. Les enveloppes de contrôle (read,
      // reaction, edit, group-update…) sont de toute façon idempotentes.
      if (m.id && seenServerIdsRef.current.has(m.id)) {
        wsRef.current?.ack(m.id);
        return;
      }
      // v4.1 : contact bloqué → on acquitte (vide la file serveur) sans rien
      // afficher ni stocker. Purement local ; l'expéditeur ne voit aucune
      // différence de comportement (pas d'accusé non plus, comme un contact
      // simplement hors-ligne de son point de vue).
      if ((blockedRef.current || []).includes(m.from)) {
        wsRef.current?.ack(m.id);
        return;
      }
      const contact = await ensureContact(m.from).catch(() => null);
      if (!contact) { handleProcessingFailure(m, new Error('annuaire inaccessible')); return; } // v4.3.1 : plus jamais silencieux

      processingStage = 'decrypt-envelope';
      const plaintext = decryptFrom(m.ciphertext, m.nonce, contact.publicKey, id.privateKey);
      processingStage = 'parse-envelope';
      const env = parseEnvelope(plaintext);

      // v4.7.0 : suppression distante applicative. L'ordre est lui-même une
      // enveloppe E2E ; le serveur ne voit ni la cible ni la nature de l'ordre.
      if (env.kind === 'delete-for-everyone') {
        const targetId = String(env.targetId || '');
        const targetPeer = env.groupId || m.from;
        if (targetId) {
          const msgs = await store.getMessages(targetPeer);
          // v4.8 : supprime toutes les anciennes copies locales du même message.
          const targets = messagesMatchingRemoteDeletion(msgs, m.from, targetId);
          for (const target of targets) await store.deleteMessage(target.localId);
          await rememberRemoteDeletion(targetId);
          if (activePeerRef.current === targetPeer) setMessages(await store.getMessages(targetPeer));
          await refreshAll();
        }
        wsRef.current.ack(m.id); pingOtherInstances(); return;
      }

      // Accusé de lecture entrant (enveloppe chiffrée, invisible du serveur).
      if (env.kind === 'read') {
        for (const sid of env.ids || []) {
          const localId = idMapRef.current.byServer.get(sid);
          if (localId !== undefined) await store.updateMessage(localId, { status: 'read' });
        }
        wsRef.current.ack(m.id);
        pingOtherInstances(); // v4.3.1
        if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
        return;
      }

      if (env.kind === 'poll-vote') {
        const targetPeer = env.groupId || m.from;
        const msgs = await store.getMessages(targetPeer);
        const target = msgs.find((x) => x.mid === env.targetId || x.serverId === env.targetId);
        const optionIndex = Number(env.optionIndex);
        if (target?.poll && Number.isInteger(optionIndex) && optionIndex >= 0 && optionIndex < target.poll.options.length) {
          const votes = { ...(target.poll.votes || {}) };
          for (const key of Object.keys(votes)) votes[key] = (votes[key] || []).filter((p) => p !== m.from);
          votes[optionIndex] = [...new Set([...(votes[optionIndex] || []), m.from])];
          await store.updateMessage(target.localId, { poll: { ...target.poll, votes } });
          if (activePeerRef.current === targetPeer) setMessages(await store.getMessages(targetPeer));
        }
        wsRef.current.ack(m.id); pingOtherInstances(); return;
      }

      // v4.0 : réaction emoji (enveloppe chiffrée ordinaire).
      if (env.kind === 'reaction') {
        const targetPeer = env.groupId || m.from;
        const msgs = await store.getMessages(targetPeer);
        const target = msgs.find((x) => x.serverId === env.targetId || x.mid === env.targetId);
        if (target) {
          const reactions = { ...(target.reactions || {}) };
          reactions[env.emoji] = [...new Set([...(reactions[env.emoji] || []), m.from])];
          await store.updateMessage(target.localId, { reactions });
          if (activePeerRef.current === targetPeer) setMessages(await store.getMessages(targetPeer));
        }
        wsRef.current.ack(m.id);
        pingOtherInstances(); // v4.3.1
        return;
      }

      // v4.1 : modification d'un message déjà envoyé (enveloppe chiffrée,
      // même principe que les réactions — le serveur voit un message banal).
      // Seul l'auteur d'origine peut éditer : on ne vérifie rien de spécial
      // ici puisque seule sa clé privée peut avoir produit un message valide
      // signé de son identité (crypto_box authentifie déjà l'expéditeur).
      if (env.kind === 'edit') {
        const targetPeer = env.groupId || m.from;
        const msgs = await store.getMessages(targetPeer);
        const target = msgs.find((x) => x.serverId === env.targetId || x.mid === env.targetId);
        if (target && target.from === m.from) {
          await store.updateMessage(target.localId, { text: env.newText, edited: true });
          if (activePeerRef.current === targetPeer) setMessages(await store.getMessages(targetPeer));
        }
        wsRef.current.ack(m.id);
        pingOtherInstances(); // v4.3.1
        return;
      }

      // v4.0 : mise à jour de groupe (renommage / membres) propagée chiffrée.
      if (env.kind === 'group-update') {
        const g = await store.getGroup(env.groupId);
        if (g) {
          await store.saveGroup({
            ...g,
            name: env.name || g.name,
            members: env.members || g.members,
          });
          await refreshAll();
          if (activePeerRef.current === env.groupId) setMessages(await store.getMessages(env.groupId));
        }
        wsRef.current.ack(m.id);
        pingOtherInstances(); // v4.3.1
        return;
      }

      // Invitation / message de groupe : le groupe est (re)créé localement à
      // partir de l'enveloppe — le serveur n'en sait rien.
      let peer = m.from;
      if (env.kind === 'group-invite' || env.kind === 'group-msg' || ((env.kind === 'poll' || env.kind === 'shopping-list') && env.groupId)) {
        peer = env.groupId;
        const existing = await store.getGroup(env.groupId);
        await store.saveGroup({
          groupId: env.groupId,
          name: env.groupName || existing?.name || 'Groupe',
          members: env.members || existing?.members || [m.from, id.pseudo],
          createdBy: existing?.createdBy || m.from,
        });
        if (env.kind === 'group-invite') {
          await refreshAll();
          wsRef.current.ack(m.id);
        pingOtherInstances(); // v4.3.1
          return;
        }
      }

      // Si une commande de suppression a été reçue avant/rejouée sur cet
      // appareil, on n'enregistre jamais ensuite le message original.
      if (env.mid && remoteDeletionIdsRef.current.includes(String(env.mid))) {
        seenServerIdsRef.current.add(m.id);
        wsRef.current.ack(m.id); pingOtherInstances(); return;
      }

      const isActive = activePeerRef.current === peer;
      const msg = {
        peer, from: m.from, msgType: m.msgType || 'text',
        text: env.text || '', sentAt: m.sentAt, read: isActive, serverId: m.id,
        mid: env.mid,                       // v4 : id logique de message (réactions/réponses)
        replyToMid: env.replyToMid,         // v4 : référence au message cité
        replyPreview: env.replyPreview,     // v4 : extrait du message cité (chiffré de bout en bout)
        important: m.important === true,
        poll: env.kind === 'poll' ? { question: String(env.question || ''), options: Array.isArray(env.options) ? env.options.map(String).slice(0, 6) : [], votes: {} } : undefined,
        shoppingList: env.kind === 'shopping-list' ? {
          title: String(env.title || 'Liste de courses').slice(0, 100),
          items: Array.isArray(env.items) ? env.items.map((x) => String(x || '').trim()).filter(Boolean).slice(0, 200) : [],
        } : undefined,
        mentions: Array.isArray(env.mentions) ? env.mentions.map(String).filter(Boolean).slice(0, 32) : [],
        mimeType: typeof env.mimeType === 'string' ? env.mimeType : '',
        ephemeralSeconds: normalizeEphemeralSeconds(env.ephemeralSeconds),
        expiresAt: Math.max(0, Number(env.expiresAt || 0) || 0),
        viewOnce: env.viewOnce === true && ['image', 'video'].includes(m.msgType || 'text'),
      };

      // Message arrivé après son expiration (appareil longtemps hors ligne,
      // programmation, etc.) : on acquitte mais on ne télécharge même pas la
      // pièce jointe. Le serveur ne voit toujours pas expiresAt, qui est dans
      // l'enveloppe chiffrée.
      if (msg.expiresAt && msg.expiresAt <= Date.now()) {
        seenServerIdsRef.current.add(m.id);
        wsRef.current.ack(m.id); pingOtherInstances(); await refreshAll(); return;
      }

      // v4.8.0 : deuxième ligne de défense contre les doublons. `mid` est
      // généré une seule fois par envoi et vit dans l'enveloppe E2E. Même si
      // une reconnexion crée exceptionnellement DEUX ids serveur différents,
      // une seule copie locale est conservée.
      if (env.mid) {
        const peerMessages = await store.getMessages(peer);
        if (hasLogicalDuplicate(peerMessages, m.from, env.mid)) {
          seenServerIdsRef.current.add(m.id);
          messageFailureCountsRef.current.delete(m.id);
          wsRef.current.ack(m.id); pingOtherInstances(); return;
        }
      }

      if (m.downloadToken) {
        processingStage = 'download-file';
        const res = await fetch(`/api/download/${m.downloadToken}`);
        if (!res.ok) throw new Error('téléchargement impossible');
        const buf = await res.arrayBuffer();
        if (m.fileNonce === 'chunked:v1') {
          // Vidéo par morceaux : trames [taille][nonce][ciphertext] déchiffrées
          // une à une — jamais de tampon monolithique de 100 Mo au déchiffrement.
          processingStage = 'decrypt-file';
          const parts = decryptChunkedFile(buf, contact.publicKey, id.privateKey);
          msg.data = parts.map((p) => p.buffer); // IndexedDB : tableau d'ArrayBuffers
          msg.fileSize = parts.reduce((n, p) => n + p.length, 0);
        } else {
          processingStage = 'decrypt-file';
          const bytes = decryptBytesFrom(buf, m.fileNonce, contact.publicKey, id.privateKey);
          msg.data = bytes.buffer;
          msg.fileSize = bytes.length;
        }
        if (m.fileName) {
          const { ciphertext, nonce } = JSON.parse(m.fileName);
          processingStage = 'decrypt-file-name';
          msg.fileName = decryptFrom(ciphertext, nonce, contact.publicKey, id.privateKey);
        } else msg.fileName = 'fichier';
      }

      processingStage = 'store-local';
      await store.saveMessage(msg);
      messageFailureCountsRef.current.delete(m.id);
      await unarchiveOnIncoming(peer);
      seenServerIdsRef.current.add(m.id); // v4.3 : jamais re-stocké en cas de retransmission
      wsRef.current.ack(m.id);
        pingOtherInstances(); // v4.3.1

      // Accusé de lecture immédiat si la conversation est ouverte et l'option active.
      if (isActive && settingsRef.current.receipts && !isGroupId(peer)) {
        sendReadReceipt(m.from, [m.id]).catch(() => {});
      }

      if (isActive) { wsRef.current?.viewed([m.id]); setMessages(await store.getMessages(peer)); }
      await refreshAll();
      if (settingsRef.current.notif && !isActive) beep();
    } catch (e) {
      console.warn('Message non traité :', e.message); // pas d'ack → retransmis (serveur, ≤ 45 s)
      handleProcessingFailure(m, e, processingStage); // visible + auto-réparation / signalement borné
    } finally {
      if (m?.id) processingServerIdsRef.current.delete(m.id);
    }
  }

  // --- v4.5.3 : appels audio E2E + qualité locale, historique local et ICE restart ---
  function replaceCall(next) {
    callRef.current = next;
    setCall(next);
  }
  function patchCall(patch) {
    const current = callRef.current;
    if (!current) return;
    replaceCall({ ...current, ...patch });
  }
  function clearCallTimers() {
    for (const timer of callTimersRef.current) clearTimeout(timer);
    callTimersRef.current.clear();
  }
  function scheduleCallTimer(fn, delay) {
    const timer = setTimeout(() => {
      callTimersRef.current.delete(timer);
      fn();
    }, delay);
    callTimersRef.current.add(timer);
    return timer;
  }
  function rememberCompletedCall(callId) {
    if (!callId) return;
    const set = completedCallIdsRef.current;
    set.add(callId);
    while (set.size > 256) set.delete(set.values().next().value);
  }
  function contactIsVerified(contact) {
    return !!(contact && typeof contact.publicKey === 'string' && contact.publicKey
      && contact.verifiedKey === contact.publicKey && Number(contact.verifiedAt || 0) > 0);
  }
  function callOutcomeFor(current, phase, message, reason) {
    if (current?.connectedAt) return 'completed';
    const text = `${message || ''} ${reason || ''}`.toLowerCase();
    if (current?.direction === 'incoming' && (text.includes('manqué') || text.includes('aucune réponse'))) return 'missed';
    if (current?.direction === 'outgoing' && text.includes('aucune réponse')) return 'no-answer';
    if (text.includes('refus')) return 'declined';
    if (text.includes('déjà en appel') || text.includes('occup')) return 'busy';
    if (phase === 'failed' || text.includes('impossible') || text.includes('interromp')) return 'failed';
    return 'cancelled';
  }
  function recordCallHistory(current, phase = 'ended', message = '', reason = '') {
    if (!current?.callId || loggedCallIdsRef.current.has(current.callId)) return;
    loggedCallIdsRef.current.add(current.callId);
    while (loggedCallIdsRef.current.size > 256) loggedCallIdsRef.current.delete(loggedCallIdsRef.current.values().next().value);
    const endedAt = Date.now();
    const connectedAt = Number(current.connectedAt || 0);
    appendCallHistory({
      peer: current.peer,
      direction: current.direction,
      media: current.mediaMode === 'video' ? 'video' : 'audio',
      outcome: callOutcomeFor(current, phase, message, reason),
      startedAt: Number(current.startedAt || endedAt),
      endedAt,
      durationSeconds: connectedAt ? Math.max(0, Math.floor((endedAt - connectedAt) / 1000)) : 0,
    });
  }
  function bufferCallCandidate(callId, candidate, fromDeviceId) {
    if (!callId || !candidate || !fromDeviceId) return;
    if (!pendingCallCandidatesRef.current.has(callId) && pendingCallCandidatesRef.current.size >= 12) {
      const first = pendingCallCandidatesRef.current.keys().next().value;
      pendingCallCandidatesRef.current.delete(first);
    }
    const list = pendingCallCandidatesRef.current.get(callId) || [];
    if (list.length < 128) list.push({ candidate, fromDeviceId });
    pendingCallCandidatesRef.current.set(callId, list);
  }
  async function flushBufferedCallCandidates(callId, session, selectedDeviceId) {
    const list = pendingCallCandidatesRef.current.get(callId) || [];
    pendingCallCandidatesRef.current.delete(callId);
    for (const candidate of candidatesForSelectedDevice(list, selectedDeviceId)) {
      await session.addRemoteCandidate(candidate);
    }
  }

  async function loadRtcConfig(client = wsRef.current) {
    if (!client?.isConnected?.()) {
      const unavailable = { enabled: false, productionReady: false, loading: false, reason: 'Connexion serveur requise.', iceServers: [] };
      setRtcInfo(unavailable); return unavailable;
    }
    try {
      const config = await client.getRtcConfig();
      const next = {
        enabled: config.enabled === true,
        productionReady: config.productionReady === true,
        stunOnly: config.stunOnly === true,
        relayOnly: config.relayOnly === true,
        iceTransportPolicy: config.iceTransportPolicy === 'all' ? 'all' : 'relay',
        reason: String(config.reason || ''),
        iceServers: Array.isArray(config.iceServers) ? config.iceServers : [],
        iceCandidatePoolSize: Number(config.iceCandidatePoolSize || 2),
        expiresAt: Number(config.expiresAt || 0),
        loading: false,
      };
      setRtcInfo(next); return next;
    } catch (error) {
      const next = { enabled: false, productionReady: false, loading: false, reason: error.message || 'Configuration d’appel indisponible.', iceServers: [] };
      setRtcInfo(next); return next;
    }
  }

  async function sendEncryptedCallSignal(peer, fields, { notify = false } = {}) {
    const me = identityRef.current;
    if (!me || !deviceIdRef.current) throw new Error('Identité d’appel indisponible.');
    if (!wsRef.current?.isConnected?.()) throw new Error('Connexion serveur requise pour la signalisation d’appel.');
    const contact = await ensureContact(peer);
    const signal = makeCallSignal({ ...fields, fromDeviceId: deviceIdRef.current });
    const encrypted = encryptFor(makeEnvelope('call-signal', { call: signal }), contact.publicKey, me.privateKey);
    wsRef.current.sendCallSignal(peer, encrypted, { notify });
    return signal;
  }

  async function closeCurrentCall({ notifyAction = '', reason = '', phase = 'ended', message = '', autoDismiss = 3500 } = {}) {
    const current = callRef.current;
    if (!current) return;
    clearCallTimers();
    stopCallTone();
    if (notifyAction) {
      await sendEncryptedCallSignal(current.peer, {
        action: notifyAction, callId: current.callId,
        toDeviceId: current.peerDeviceId || undefined, reason,
      }).catch(() => {});
    }
    const session = callSessionRef.current;
    callSessionRef.current = null;
    if (session) await session.close(false).catch(() => {});
    pendingCallCandidatesRef.current.delete(current.callId);
    recordCallHistory(current, phase, message, reason);
    rememberCompletedCall(current.callId);
    replaceCall({ ...current, phase, message, error: phase === 'failed' ? (reason || message) : '', remoteStream: null, localStream: null });
    if (autoDismiss > 0) scheduleCallTimer(() => { if (callRef.current?.callId === current.callId) replaceCall(null); }, autoDismiss);
  }

  function createMediaCallSession({ callId, peer, config = rtcInfo, mode = 'audio' }) {
    const session = new AudioCallSession({
      callId,
      peer,
      rtcConfig: config,
      mode,
      sendSignal: async ({ action, description, candidate, reason }) => {
        const current = callRef.current;
        if (!current || current.callId !== callId) throw new Error('Appel devenu inactif.');
        return sendEncryptedCallSignal(peer, {
          action, callId, description, candidate, reason,
          // Le type audio/vidéo reste à l'intérieur du ciphertext E2E.
          media: current.mediaMode === 'video' ? 'video' : 'audio',
          toDeviceId: action === 'offer' ? undefined : (current.peerDeviceId || undefined),
        }, { notify: action === 'offer' });
      },
      onRemoteStream: (stream) => {
        if (callRef.current?.callId === callId) patchCall({ remoteStream: stream || null });
      },
      onLocalStream: (stream) => {
        if (callRef.current?.callId === callId) patchCall({ localStream: stream || null });
      },
      onDiagnostic: (diagnostic) => {
        // Diagnostic local uniquement : aucune IP/port/pseudo/callId n'est
        // contenu dans l'objet produit par audio-call.js et rien n'est envoyé
        // au serveur. Une écriture localStorage impossible est sans impact.
        try { localStorage.setItem('messagerie:last-audio-network:v1', sealLocalString('audio-network', JSON.stringify(diagnostic))); } catch (_) {}
        if (callRef.current?.callId === callId) patchCall({ networkRoute: diagnostic });
      },
      onQuality: (quality) => {
        // Qualité média locale seulement (latence/jitter/pertes agrégés).
        try { localStorage.setItem('messagerie:last-audio-quality:v1', sealLocalString('audio-quality', JSON.stringify(quality))); } catch (_) {}
        if (callRef.current?.callId === callId) patchCall({ quality });
      },
      onState: (state) => {
        const current = callRef.current;
        if (!current || current.callId !== callId) return;
        if (state === 'connected') {
          clearCallTimers();
          patchCall({ phase: 'connected', connectedAt: current.connectedAt || Date.now(), message: '', error: '', reconnecting: false });
        } else if (state === 'connecting') {
          patchCall({ phase: 'connecting', message: 'Connexion sécurisée…' });
        } else if (state === 'failed') {
          if (current.direction === 'outgoing' && current.connectedAt && Number(current.restartAttempts || 0) < 2 && callSessionRef.current) {
            patchCall({ phase: 'connected', reconnecting: true, message: 'Reconnexion réseau…', restartAttempts: Number(current.restartAttempts || 0) + 1 });
            callSessionRef.current.restartIceAsOffer().catch(() => {
              closeCurrentCall({ notifyAction: 'failed', phase: 'failed', reason: 'Connexion d’appel impossible.', autoDismiss: 7000 });
            });
          } else {
            closeCurrentCall({ notifyAction: 'failed', phase: 'failed', reason: 'Connexion d’appel impossible.', autoDismiss: 7000 });
          }
        } else if (state === 'disconnected' && current.phase === 'connected') {
          patchCall({ message: 'Réseau instable — reconnexion…', reconnecting: true });
          // Seul l'appelant initie l'ICE restart : pas de collision de
          // renégociation. L'offre/reponse de restart reste dans le même canal
          // de signalisation E2E que l'appel initial.
          if (current.direction === 'outgoing' && Number(current.restartAttempts || 0) < 2) {
            scheduleCallTimer(async () => {
              const latest = callRef.current;
              const session = callSessionRef.current;
              if (!latest || latest.callId !== callId || latest.phase !== 'connected' || !session) return;
              const stillDown = ['disconnected', 'failed'].includes(session.pc?.connectionState)
                || ['disconnected', 'failed'].includes(session.pc?.iceConnectionState);
              if (!stillDown) return;
              patchCall({ restartAttempts: Number(latest.restartAttempts || 0) + 1, message: 'Changement de réseau — reconnexion sécurisée…' });
              try { await session.restartIceAsOffer(); }
              catch (error) { patchCall({ error: error?.message || 'Reconnexion réseau impossible.' }); }
            }, 1500);
          }
          scheduleCallTimer(() => {
            const latest = callRef.current;
            const session = callSessionRef.current;
            if (latest?.callId === callId && latest.phase === 'connected'
                && (['disconnected', 'failed'].includes(session?.pc?.connectionState)
                  || ['disconnected', 'failed'].includes(session?.pc?.iceConnectionState))) {
              closeCurrentCall({ notifyAction: 'failed', phase: 'failed', reason: 'Connexion d’appel interrompue.', autoDismiss: 7000 });
            }
          }, 18000);
        }
      },
      onError: (error) => {
        if (callRef.current?.callId === callId) patchCall({ error: error?.message || 'Erreur d’appel.' });
      },
    });
    return session;
  }

  async function handleCallSignal(id, packet) {
    const signalId = String(packet?.id || '');
    if (signalId && seenCallSignalIdsRef.current.has(signalId)) {
      wsRef.current?.ackCallSignal(signalId); return;
    }
    // Un signal chiffré peut cibler un appareil précis. Les autres appareils du
    // même compte le reçoivent aussi (le serveur ne connaît pas le deviceId),
    // mais NE DOIVENT PAS l'acquitter : sinon le premier appareil non ciblé
    // supprimerait le signal avant que l'appareil choisi puisse se reconnecter.
    let shouldAck = false;
    try {
      if ((blockedRef.current || []).includes(packet.from)) { shouldAck = true; return; }
      const contact = await ensureContact(packet.from);
      const plaintext = decryptFrom(packet.ciphertext, packet.nonce, contact.publicKey, id.privateKey);
      const env = parseEnvelope(plaintext);
      if (env.kind !== 'call-signal') { shouldAck = true; return; }
      const signal = normalizeCallSignal(env.call);
      if (!signal) { shouldAck = true; return; }
      if (!signalTargetsDevice(signal, deviceIdRef.current)) return;
      // `selected` est diffusé à tous pour fermer les appareils non choisis,
      // mais seul l'appareil sélectionné l'acquitte afin qu'une reconnexion
      // transitoire de celui-ci ne perde pas la décision du correspondant.
      shouldAck = shouldAcknowledgeCallSignal(signal, deviceIdRef.current);
      const current = callRef.current;

      // Tous les signaux tardifs d'un appel déjà terminé sont idempotemment
      // ignorés. Sans cette garde, une réponse retardée pouvait brièvement
      // ressusciter l'interface après un refus/timeout alors que le micro et la
      // peer connection avaient déjà été libérés.
      if (completedCallIdsRef.current.has(signal.callId)
          || (current?.callId === signal.callId && ['ended', 'failed'].includes(current.phase))) {
        rememberCompletedCall(signal.callId);
        pendingCallCandidatesRef.current.delete(signal.callId);
        return;
      }

      // Un raccrochage/annulation peut précéder l'offre après une reconnexion
      // (plusieurs signaux créés dans la même milliseconde). On mémorise alors
      // le callId pour qu'une offre plus ancienne livrée ensuite ne fasse pas
      // sonner un appel déjà terminé.
      if ((!current || current.callId !== signal.callId || current.peer !== packet.from)
          && isTerminalCallAction(signal.action)) {
        rememberCompletedCall(signal.callId);
        pendingCallCandidatesRef.current.delete(signal.callId);
        return;
      }

      if (signal.action === 'candidate') {
        if (current?.callId === signal.callId) {
          // Tant que l'appareil distant n'est pas sélectionné (plusieurs
          // téléphones peuvent répondre au même compte), on garde les
          // candidats dans un tampon identifié par deviceId. Après sélection,
          // seuls ceux de l'appareil retenu atteignent WebRTC.
          if (!current.peerDeviceId) {
            bufferCallCandidate(signal.callId, signal.candidate, signal.fromDeviceId);
          } else if (candidateComesFromSelectedDevice(signal, current.peerDeviceId)) {
            if (callSessionRef.current) await callSessionRef.current.addRemoteCandidate(signal.candidate);
            else bufferCallCandidate(signal.callId, signal.candidate, signal.fromDeviceId);
          }
        } else {
          // Un candidat trickle peut précéder l'offre sur le WebSocket.
          bufferCallCandidate(signal.callId, signal.candidate, signal.fromDeviceId);
        }
        return;
      }

      if (signal.action === 'offer') {
        if (completedCallIdsRef.current.has(signal.callId)) {
          pendingCallCandidatesRef.current.delete(signal.callId);
          return;
        }
        if (current && !['ended', 'failed'].includes(current.phase) && current.callId !== signal.callId) {
          // Collision d'appels (« glare ») : si les deux personnes appellent au
          // même instant, un ordre déterministe sur les UUID choisit un seul
          // appel. Sans cela, les deux côtés répondraient « occupé » et aucun
          // appel ne démarrerait.
          const incomingWins = current.direction === 'outgoing' && signal.callId.localeCompare(current.callId) < 0;
          if (incomingWins) {
            await closeCurrentCall({ notifyAction: 'cancel', reason: 'Collision d’appels.', phase: 'ended', message: '', autoDismiss: 0 });
          } else {
            await sendEncryptedCallSignal(packet.from, { action: 'busy', callId: signal.callId, toDeviceId: signal.fromDeviceId, reason: 'Déjà en appel.' }).catch(() => {});
            return;
          }
        }
        if (current?.callId === signal.callId) {
          await sendEncryptedCallSignal(packet.from, { action: 'ringing', callId: signal.callId, toDeviceId: signal.fromDeviceId }).catch(() => {});
          return;
        }
        clearCallTimers();
        const mediaMode = signal.media === 'video' ? 'video' : 'audio';
        replaceCall({
          phase: 'incoming', direction: 'incoming', peer: packet.from,
          callId: signal.callId, peerDeviceId: signal.fromDeviceId,
          offer: signal.description, mediaMode, muted: false, videoEnabled: mediaMode === 'video',
          remoteStream: null, localStream: null,
          startedAt: Date.now(), connectedAt: 0, restartAttempts: 0, reconnecting: false,
          quality: null, networkRoute: null, verified: contactIsVerified(contact), message: '', error: '',
        });
        await sendEncryptedCallSignal(packet.from, { action: 'ringing', callId: signal.callId, toDeviceId: signal.fromDeviceId }).catch(() => {});
        scheduleCallTimer(() => {
          if (callRef.current?.callId === signal.callId && callRef.current.phase === 'incoming') {
            closeCurrentCall({ notifyAction: 'decline', reason: 'Aucune réponse.', phase: 'ended', message: 'Appel manqué', autoDismiss: 4500 });
          }
        }, 45000);
        try { navigator.vibrate?.([250, 120, 250]); } catch (_) {}
        return;
      }

      if (!current || current.callId !== signal.callId || current.peer !== packet.from) return;

      if (signal.action === 'ringing' && current.direction === 'outgoing') {
        patchCall({ phase: 'ringing', message: 'Le téléphone sonne…' }); return;
      }
      if (signal.action === 'answer' && current.direction === 'outgoing') {
        if (current.peerDeviceId && current.peerDeviceId !== signal.fromDeviceId) {
          await sendEncryptedCallSignal(current.peer, { action: 'selected', callId: current.callId, selectedDeviceId: current.peerDeviceId }).catch(() => {});
          return;
        }
        patchCall({ peerDeviceId: signal.fromDeviceId, phase: 'connecting', message: 'Connexion sécurisée…' });
        await callSessionRef.current?.applyAnswer(signal.description);
        if (callSessionRef.current) await flushBufferedCallCandidates(current.callId, callSessionRef.current, signal.fromDeviceId);
        await sendEncryptedCallSignal(current.peer, { action: 'selected', callId: current.callId, selectedDeviceId: signal.fromDeviceId }).catch(() => {});
        return;
      }
      if (signal.action === 'selected' && current.direction === 'incoming') {
        if (signal.selectedDeviceId !== deviceIdRef.current) {
          await closeCurrentCall({ phase: 'ended', message: 'Appel pris sur un autre appareil', autoDismiss: 3000 });
        } else patchCall({ phase: 'connecting', message: 'Connexion sécurisée…' });
        return;
      }
      if (signal.action === 'restart-offer') {
        if (current.peerDeviceId && current.peerDeviceId !== signal.fromDeviceId) return;
        if (!callSessionRef.current) return;
        patchCall({ message: 'Reconnexion réseau sécurisée…', reconnecting: true });
        await callSessionRef.current.acceptRestartOffer(signal.description);
        return;
      }
      if (signal.action === 'restart-answer' && current.direction === 'outgoing') {
        if (current.peerDeviceId && current.peerDeviceId !== signal.fromDeviceId) return;
        if (!callSessionRef.current) return;
        await callSessionRef.current.applyRestartAnswer(signal.description);
        return;
      }
      if (signal.action === 'decline') return closeCurrentCall({ phase: 'ended', message: 'Appel refusé', autoDismiss: 4000 });
      if (signal.action === 'busy') return closeCurrentCall({ phase: 'ended', message: 'Contact déjà en appel', autoDismiss: 4000 });
      if (signal.action === 'cancel') return closeCurrentCall({ phase: 'ended', message: 'Appel annulé', autoDismiss: 3000 });
      if (signal.action === 'hangup') return closeCurrentCall({ phase: 'ended', message: 'Appel terminé', autoDismiss: 3000 });
      if (signal.action === 'failed') return closeCurrentCall({ phase: 'failed', reason: signal.reason || 'Appel interrompu.', autoDismiss: 7000 });
    } catch (error) {
      // Un signal indéchiffrable/invalide ne deviendra jamais valide sur ce
      // même appareil : on l'acquitte pour éviter une boucle de relivraison.
      shouldAck = true;
      setBanner(`Signal d’appel non traité : ${error.message || 'signal invalide'}`);
      setTimeout(() => setBanner(''), 5000);
    } finally {
      if (signalId && shouldAck) {
        seenCallSignalIdsRef.current.add(signalId);
        if (seenCallSignalIdsRef.current.size > 2000) seenCallSignalIdsRef.current.delete(seenCallSignalIdsRef.current.values().next().value);
        wsRef.current?.ackCallSignal(signalId);
      }
    }
  }

  async function startMediaCall(mediaMode = 'audio', peerOverride = '') {
    const mode = mediaMode === 'video' ? 'video' : 'audio';
    const peer = String(peerOverride || activePeer || '').trim();
    if (!peer || isGroupId(peer) || peer === identity.pseudo) return;
    if (callRef.current && !['ended', 'failed'].includes(callRef.current.phase)) throw new Error('Un appel est déjà en cours.');
    if (keyAlert?.pseudo === peer) throw new Error('Vérifiez d’abord la nouvelle clé du contact.');
    if ((blockedRef.current || []).includes(peer)) throw new Error('Débloquez ce contact avant de l’appeler.');
    if (mode === 'video' ? !supportsVideoCalls() : !supportsAudioCalls()) {
      throw new Error(mode === 'video' ? 'Les appels vidéo ne sont pas pris en charge sur ce navigateur.' : 'Les appels audio ne sont pas pris en charge sur ce navigateur.');
    }
    // Geste utilisateur : on amorce Web Audio avant l'aller-retour de
    // signalisation, afin que Safari puisse jouer le retour de sonnerie.
    await primeCallAudio().catch(() => false);
    // Valide le contact et sa clé AVANT d'ouvrir microphone/caméra, même si
    // l'appel est lancé depuis la liste sans ouvrir la conversation.
    const callContact = await ensureContact(peer);
    let config = rtcInfo;
    if (!config.enabled || (config.expiresAt && config.expiresAt < Date.now() + 60000)) config = await loadRtcConfig();
    if (!config.enabled) throw new Error(config.reason || 'Appels indisponibles.');
    const callId = crypto.randomUUID();
    clearCallTimers();
    replaceCall({
      phase: 'outgoing', direction: 'outgoing', peer, callId, mediaMode: mode,
      peerDeviceId: '', muted: false, videoEnabled: mode === 'video', remoteStream: null, localStream: null,
      startedAt: Date.now(), connectedAt: 0, restartAttempts: 0, reconnecting: false,
      quality: null, networkRoute: null, verified: contactIsVerified(callContact),
      message: config.stunOnly ? 'Mode réseau limité (TURN absent)…' : '', error: '',
    });
    const session = createMediaCallSession({ callId, peer, config, mode });
    callSessionRef.current = session;
    scheduleCallTimer(() => {
      if (callRef.current?.callId === callId && ['outgoing', 'ringing', 'connecting'].includes(callRef.current.phase)) {
        closeCurrentCall({ notifyAction: 'cancel', reason: 'Délai dépassé.', phase: 'ended', message: 'Aucune réponse', autoDismiss: 12000 });
      }
    }, 60000);
    try { await session.startOutgoing(); }
    catch (error) { await closeCurrentCall({ phase: 'failed', reason: error.message || 'Impossible de démarrer l’appel.', autoDismiss: 8000 }); }
  }

  async function startAudioCall(peer = '') { return startMediaCall('audio', peer); }
  async function startVideoCall(peer = '') { return startMediaCall('video', peer); }

  async function acceptAudioCall() {
    const current = callRef.current;
    if (!current || current.phase !== 'incoming' || !current.offer) return;
    const mode = current.mediaMode === 'video' ? 'video' : 'audio';
    clearCallTimers();
    patchCall({ phase: 'connecting', message: mode === 'video' ? 'Activation du micro et de la caméra…' : 'Activation du microphone…', error: '' });
    try {
      if (mode === 'video' ? !supportsVideoCalls() : !supportsAudioCalls()) throw new Error(`Les appels ${mode === 'video' ? 'vidéo' : 'audio'} ne sont pas pris en charge sur ce navigateur.`);
      let config = rtcInfo;
      if (!config.enabled || (config.expiresAt && config.expiresAt < Date.now() + 60000)) config = await loadRtcConfig();
      if (!config.enabled) throw new Error(config.reason || 'Appels indisponibles.');
      const session = createMediaCallSession({ callId: current.callId, peer: current.peer, config, mode });
      callSessionRef.current = session;
      await session.acceptOffer(current.offer);
      await flushBufferedCallCandidates(current.callId, session, current.peerDeviceId);
      scheduleCallTimer(() => {
        if (callRef.current?.callId === current.callId && callRef.current.phase === 'connecting') {
          closeCurrentCall({ notifyAction: 'failed', phase: 'failed', reason: 'Connexion d’appel impossible.', autoDismiss: 7000 });
        }
      }, 35000);
    } catch (error) {
      await closeCurrentCall({ notifyAction: 'failed', phase: 'failed', reason: error.message || (mode === 'video' ? 'Caméra ou microphone indisponible.' : 'Microphone indisponible.'), autoDismiss: 8000 });
    }
  }

  async function declineAudioCall() {
    const current = callRef.current;
    if (!current) return;
    await closeCurrentCall({ notifyAction: 'decline', reason: 'Appel refusé.', phase: 'ended', message: 'Appel refusé', autoDismiss: 1200 });
  }
  async function hangupAudioCall() {
    const current = callRef.current;
    if (!current) return;
    const action = ['outgoing', 'ringing'].includes(current.phase) ? 'cancel' : 'hangup';
    await closeCurrentCall({ notifyAction: action, reason: 'Appel terminé.', phase: 'ended', message: 'Appel terminé', autoDismiss: 2500 });
  }
  function toggleCallMute() {
    const session = callSessionRef.current;
    const muted = session ? session.setMuted(!callRef.current?.muted) : !callRef.current?.muted;
    patchCall({ muted });
  }
  function toggleCallVideo() {
    const session = callSessionRef.current;
    if (!session || callRef.current?.mediaMode !== 'video') return;
    const videoEnabled = session.setVideoEnabled(!callRef.current?.videoEnabled);
    patchCall({ videoEnabled });
  }
  async function switchCallCamera() {
    const session = callSessionRef.current;
    if (!session || callRef.current?.mediaMode !== 'video') return;
    try {
      const facingMode = await session.switchCamera();
      patchCall({ facingMode, localStream: session.localStream || null });
    } catch (error) { patchCall({ error: error?.message || 'Impossible de changer de caméra.' }); }
  }
  async function leaveVoiceMessageAfterCall() {
    const current = callRef.current;
    if (!current?.peer || current.direction !== 'outgoing' || current.connectedAt) return;
    const text = `${current.message || ''} ${current.error || ''}`;
    if (!/aucune réponse|sans réponse/i.test(text)) return;
    const peer = current.peer;
    await dismissAudioCall();
    try {
      await openConversation(peer);
      setVoiceMessageRequest({ peer, token: Date.now() });
    } catch (error) {
      setBanner(error?.message || 'Impossible d’ouvrir la conversation pour laisser un vocal.');
      setTimeout(() => setBanner(''), 5000);
    }
  }

  async function dismissAudioCall() {
    if (callRef.current?.callId) rememberCompletedCall(callRef.current.callId);
    const session = callSessionRef.current;
    callSessionRef.current = null;
    if (session) await session.close(false).catch(() => {});
    clearCallTimers(); replaceCall(null);
  }

  // Un changement de page/fermeture doit toujours libérer microphone et caméra.
  useEffect(() => {
    const stop = () => {
      const current = callRef.current;
      if (current && !['ended', 'failed'].includes(current.phase)) recordCallHistory(current, 'ended', 'App interrompu par fermeture', 'pagehide');
      callSessionRef.current?.close(true, 'pagehide').catch(() => {});
    };
    window.addEventListener('pagehide', stop);
    return () => window.removeEventListener('pagehide', stop);
  }, []);

  // Accusé de lecture : enveloppe chiffrée ordinaire — le serveur voit un
  // message 1-à-1 banal, pas un accusé. Jamais envoyé si l'option est OFF.
  async function sendReadReceipt(toPseudo, serverIds) {
    const contact = await ensureContact(toPseudo);
    const enc = encryptFor(makeEnvelope('read', { ids: serverIds }), contact.publicKey, identity.privateKey);
    wsRef.current.send({ type: 'send', to: toPseudo, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
  }

  // --- Ouverture d'une conversation (1-à-1 ou groupe) ---
  async function openConversation(peer, targetMessageId = null) {
    if (!isGroupId(peer)) {
      const contact = await ensureContact(peer);
      // TOFU : détection de changement de clé publique par rapport à la valeur
      // mémorisée au premier contact. En cas de différence : alerte BLOQUANTE.
      try {
        const res = await fetch(`/api/users/${encodeURIComponent(peer)}`);
        if (res.ok) {
          const u = await res.json();
          if (u.publicKey !== contact.publicKey) {
            setKeyAlert({
              pseudo: peer,
              oldFp: fingerprint(contact.publicKey),
              newFp: fingerprint(u.publicKey),
              newKey: u.publicKey,
            });
          } else setKeyAlert(null);
        }
      } catch (_) { /* hors ligne : on garde la clé mémorisée */ }
      wsRef.current?.askPresence(peer);
    } else setKeyAlert(null);

    await store.markConversationRead(peer);
    setActivePeer(peer);
    setFocusMessageId(targetMessageId ?? null);
    const msgs = (await store.getMessages(peer)).filter((m) => !(Number(m.expiresAt || 0) > 0 && Number(m.expiresAt) <= Date.now()));
    const viewedIds = msgs.filter((x) => x.from !== identity.pseudo && x.serverId).map((x) => x.serverId);
    wsRef.current?.viewed(viewedIds);
    setMessages(msgs);
    await refreshAll();

    // Accusés de lecture différés (messages reçus pendant que la conversation
    // était fermée), si l'option est active — 1-à-1 uniquement.
    if (settingsRef.current.receipts && !isGroupId(peer)) {
      const ids = msgs.filter((x) => x.from === peer && x.serverId && !x.receiptSent).map((x) => x.serverId);
      if (ids.length) {
        sendReadReceipt(peer, ids).catch(() => {});
        for (const x of msgs.filter((y) => ids.includes(y.serverId))) {
          await store.updateMessage(x.localId, { receiptSent: true });
        }
      }
    }
  }

  function acceptNewKey() {
    // L'utilisateur a vérifié le nouveau fingerprint par un canal externe.
    store.saveContact({
      pseudo: keyAlert.pseudo, publicKey: keyAlert.newKey,
      verifiedKey: keyAlert.newKey, verifiedAt: Date.now(), verifiedMethod: 'manual',
    }).then(() => refreshAll());
    setKeyAlert(null);
  }

  // --- Création de groupe (aucune donnée serveur : invitations chiffrées) ---
  async function createGroup(name, memberPseudos) {
    const members = [...new Set(memberPseudos.filter((p) => p && p !== identity.pseudo))];
    if (members.length < 1) throw new Error('Ajoutez au moins un membre.');
    if (members.length > 8) throw new Error('8 membres maximum (hors vous).');
    const resolved = [];
    for (const p of members) resolved.push(await ensureContact(p)); // vérifie l'existence
    const groupId = `grp:${crypto.randomUUID()}`;
    const allMembers = [identity.pseudo, ...members];
    await store.saveGroup({ groupId, name, members: allMembers, createdBy: identity.pseudo });
    // v4.3.2 : protection par membre (cohérence avec updateGroup) — un envoi
    // qui échoue (ex. race d'authentification pendant une reconnexion, cf.
    // client/src/ws.js) ne doit plus faire échouer TOUTE la création de
    // groupe pour TOUS les membres. Le groupe existe déjà localement
    // (saveGroup ci-dessus) ; un membre non invité recevra le groupe via un
    // futur échange (mécanisme déjà en place pour group-update).
    for (const c of resolved) {
      try {
        const enc = encryptFor(
          makeEnvelope('group-invite', { groupId, groupName: name, members: allMembers }),
          c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: c.pseudo, msgType: 'text', tmpId: crypto.randomUUID(), ...enc });
      } catch (_) { /* injoignable/reconnexion en cours : ignoré pour cet envoi */ }
    }
    await refreshAll();
    return groupId;
  }

  // --- Envoi (1-à-1 ou fan-out de groupe) ---
  async function sendMessage(text, file, onProgress, options = {}) {
    const important = options.important === true;
    const mentions = Array.isArray(options.mentions) ? options.mentions.map(String).filter(Boolean).slice(0, 32) : [];
    const deliverAt = Number.isFinite(Number(options.deliverAt)) && Number(options.deliverAt) > Date.now() ? Number(options.deliverAt) : null;
    const ephemeralSeconds = normalizeEphemeralSeconds(options.ephemeralSeconds);
    const requestedViewOnce = options.viewOnce === true;
    const expiresAt = computeExpiresAt({ ephemeralSeconds, deliverAt, now: Date.now() });
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    if (keyAlert) throw new Error('Clé du contact modifiée — vérifiez le nouveau fingerprint avant d’envoyer.');
    if (archivedConversationsRef.current.includes(activePeer)) await setConversationArchived(activePeer, false);

    // v4.1 : « Notes personnelles » (conversation avec soi-même) — traitée
    // ENTIÈREMENT en local, aucun aller-retour serveur. Plus simple, plus
    // privé (zéro métadonnée, pas même un relais éphémère), et ça évite un
    // doublon qui se produirait sinon (le message reviendrait vers soi via
    // le protocole normal de livraison, en plus de la copie locale déjà
    // enregistrée par l'envoi lui-même).
    if (!group && activePeer === identity.pseudo) {
      const mid = crypto.randomUUID();
      const localMsg = {
        peer: activePeer, from: identity.pseudo, msgType: 'text',
        text, sentAt: Date.now(), read: true, status: 'sent', mid, important, deliverAt, mentions, ephemeralSeconds, expiresAt,
        total: 0, sentCount: 0, deliveredCount: 0, failedCount: 0, serverIds: [],
      };
      if (file) {
        const buf = await file.arrayBuffer();
        let msgType, sendName = file.name;
        if (file.__forceType === 'audio') { msgType = 'audio'; }
        else {
          const check = classifyFile(file.name, buf);
          if (!check.ok) throw new Error(check.error);
          msgType = check.msgType;
        }
        const limit = msgType === 'video' ? VIDEO_MAX : FILE_MAX;
        if (buf.byteLength > limit) {
          throw new Error(msgType === 'video' ? 'Vidéo trop volumineuse (100 Mo max).' : 'Fichier trop volumineux (20 Mo max).');
        }
        let finalBuf = buf;
        if (msgType === 'image') {
          const out = await maybeCompressImage(file, buf);
          finalBuf = out.buf; sendName = out.name;
        }
        localMsg.msgType = msgType;
        localMsg.fileName = sendName;
        localMsg.fileSize = finalBuf.byteLength;
        localMsg.mimeType = file.__mimeType || file.type || '';
        localMsg.viewOnce = requestedViewOnce && (msgType === 'image' || msgType === 'video');
        localMsg.data = finalBuf;
      }
      await store.saveMessage(localMsg);
      setMessages(await store.getMessages(activePeer));
      await refreshAll();
      setReplyTo(null);
      onProgress?.(1);
      return;
    }

    // v3.1 — CORRECTIF GROUPES : la résolution des membres était « tout ou
    // rien » (Promise.all) AVANT le moindre envoi : un seul membre introuvable
    // (pseudo purgé après 7 jours d'inactivité, compte recréé…) faisait
    // échouer TOUT l'envoi pour TOUT le monde, silencieusement. Désormais :
    // résolution individuelle, les membres injoignables sont ignorés et
    // signalés, les autres reçoivent normalement.
    let recipients = [];
    const unreachable = [];
    if (group) {
      for (const p of (group.members || []).filter((x) => x !== identity.pseudo)) {
        try { recipients.push(await ensureContact(p)); } catch (_) { unreachable.push(p); }
      }
      if (recipients.length === 0) {
        throw new Error('Aucun membre du groupe n’est joignable (comptes purgés ou supprimés).');
      }
      if (unreachable.length) {
        setBanner(`Membre(s) injoignable(s), ignoré(s) pour cet envoi : ${unreachable.join(', ')} (compte purgé pour inactivité ?)`);
        setTimeout(() => setBanner(''), 6000);
      }
    } else {
      recipients = [await ensureContact(activePeer)];
    }

    const tmpIds = recipients.map(() => crypto.randomUUID());
    const mid = crypto.randomUUID(); // id logique stable (réactions/réponses)
    const reply = replyTo ? {
      replyToMid: replyTo.mid || replyTo.serverId,
      replyPreview: replyTo.expiresAt ? 'Message éphémère' : (replyTo.text || replyTo.fileName || 'pièce jointe').slice(0, 80),
    } : {};
    const localMsg = {
      peer: activePeer, from: identity.pseudo, msgType: 'text',
      text, sentAt: Date.now(), read: true, status: deliverAt ? 'scheduled' : 'sending', mid, ...reply, important, deliverAt, mentions, ephemeralSeconds, expiresAt,
      total: recipients.length, sentCount: 0, deliveredCount: 0, failedCount: 0, serverIds: [],
    };

    let buf = null, msgType = 'text', sendName = null, viewOnce = false;
    if (file) {
      buf = await file.arrayBuffer();
      let check;
      if (file.__forceType === 'audio') {
        // Enregistrement vocal produit localement par l'app (MediaRecorder) :
        // pas un fichier externe non fiable, donc pas besoin de sniffer les
        // magic bytes — on force directement le type. Évite au passage une
        // confusion avec la détection webm générique (vidéo), qui partage la
        // même signature EBML que l'audio webm.
        check = { ok: true, msgType: 'audio' };
      } else {
        check = classifyFile(file.name, buf);
      }
      if (!check.ok) throw new Error(check.error);
      msgType = check.msgType;
      // v4.3 : vidéos AUTORISÉES en groupe, mais limitées à 20 Mo (courtes) —
      // le fan-out chiffre et téléverse une copie PAR membre : une vidéo de
      // 100 Mo × 8 membres saturerait mobile et VPS. Côté serveur, les vidéos
      // jamais acquittées sont purgées après 6 h (TTL court, cf. ws.js).
      if (msgType === 'video' && group && buf.byteLength > FILE_MAX) {
        throw new Error('En groupe, vidéos limitées à 20 Mo (formats courts).');
      }
      const limit = msgType === 'video' ? VIDEO_MAX : FILE_MAX;
      if (buf.byteLength > limit) {
        throw new Error(msgType === 'video' ? 'Vidéo trop volumineuse (100 Mo max).' : 'Fichier trop volumineux (20 Mo max).');
      }
      sendName = file.name;
      if (msgType === 'image') {
        // Compression AVANT chiffrement (jamais l'inverse).
        const out = await maybeCompressImage(file, buf);
        buf = out.buf; sendName = out.name;
      }
      localMsg.msgType = msgType;
      localMsg.fileName = sendName;
      localMsg.fileSize = buf.byteLength;
      localMsg.mimeType = file.__mimeType || file.type || '';
      viewOnce = requestedViewOnce && (msgType === 'image' || msgType === 'video');
      localMsg.viewOnce = viewOnce;
      localMsg.data = buf;
    }

    const localId = await store.saveMessage(localMsg);
    tmpIds.forEach((t) => idMapRef.current.byTmp.set(t, localId));
    setMessages(await store.getMessages(activePeer));

    // v3.1 : chaque destinataire est INDÉPENDANT — un échec sur l'un ne doit
    // jamais empêcher l'envoi aux autres ni marquer tout le message en échec.
    let failedFor = [];
    for (let r = 0; r < recipients.length; r++) {
      const contact = recipients[r];
      try {
        await verifyContactKey(contact); // v4.3.1 : jamais chiffrer avec une clé périmée
        const envKind = group ? 'group-msg' : 'text';
        const envFields = group
          ? { groupId: group.groupId, groupName: group.name, members: group.members, text, mid, mentions, mimeType: file ? (file.__mimeType || file.type || '') : '', ephemeralSeconds, expiresAt, viewOnce, ...reply }
          : { text, mid, mentions, mimeType: file ? (file.__mimeType || file.type || '') : '', ephemeralSeconds, expiresAt, viewOnce, ...reply };
        const enc = encryptFor(makeEnvelope(envKind, envFields), contact.publicKey, identity.privateKey);
        const payload = { type: 'send', to: contact.pseudo, msgType, tmpId: tmpIds[r], important, deliverAt, ...enc };

        if (file) {
          const base = r / recipients.length, span = 1 / recipients.length;
          if (msgType === 'video' && buf.byteLength > FILE_MAX) {
            // Upload PAR MORCEAUX : 5 Mo par trame, chacune avec son propre nonce.
            const token = await wsRef.current.getUploadToken();
            const n = Math.ceil(buf.byteLength / CHUNK_SIZE);
            for (let i = 0; i < n; i++) {
              const frame = encryptChunk(buf.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE),
                contact.publicKey, identity.privateKey);
              const res = await fetch(`/api/upload-chunk/${token}`, {
                method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: frame,
              });
              if (!res.ok) throw new Error((await res.json()).error || 'Envoi impossible.');
              onProgress?.(base + span * ((i + 1) / n) * 0.95);
            }
            const fin = await fetch(`/api/upload-finish/${token}`, { method: 'POST' });
            if (!fin.ok) throw new Error('Finalisation impossible.');
            payload.fileId = (await fin.json()).fileId;
            payload.fileNonce = 'chunked:v1';
          } else {
            const encFile = encryptBytesFor(buf, contact.publicKey, identity.privateKey);
            onProgress?.(base + span * 0.5);
            const token = await wsRef.current.getUploadToken();
            const res = await fetch(`/api/upload/${token}`, {
              method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: encFile.ciphertext,
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Envoi impossible.');
            payload.fileId = (await res.json()).fileId;
            payload.fileNonce = encFile.nonce;
            onProgress?.(base + span * 0.9);
          }
          const encName = encryptFor(sendName, contact.publicKey, identity.privateKey);
          payload.fileName = JSON.stringify(encName);
          payload.fileSize = buf.byteLength;
        }
        wsRef.current.send(payload);
      } catch (e) {
        // v3.1 : échec limité à CE destinataire — les autres continuent.
        failedFor.push({ pseudo: contact.pseudo, error: e.message });
        idMapRef.current.byTmp.delete(tmpIds[r]);
      }
    }

    if (failedFor.length) {
      // Échec TOTAL → statut failed + erreur ; échec PARTIEL → le message vit,
      // les compteurs intègrent les échecs, l'utilisateur est informé.
      await store.mutateMessage(localId, (msg) => ({
        ...msg,
        failedCount: failedFor.length,
        failedFor: failedFor.map((f) => f.pseudo),
        status: failedFor.length >= recipients.length ? 'failed' : msg.status,
      }));
      setMessages(await store.getMessages(activePeer));
      if (failedFor.length >= recipients.length) {
        throw new Error(failedFor[0].error || 'Envoi impossible.');
      }
      setBanner(`Envoi échoué vers : ${failedFor.map((f) => f.pseudo).join(', ')} — les autres membres ont bien reçu.`);
      setTimeout(() => setBanner(''), 6000);
    }
    setMessages(await store.getMessages(activePeer));
    await refreshAll();
    setReplyTo(null);
    onProgress?.(1);
  }

  // --- v4.0 : file d'envoi globale (compteur) ---
  async function sendWithQueue(text, file, onProgress, options = {}) {
    setSendQueue((n) => n + 1);
    try { await sendMessage(text, file, onProgress, options); }
    finally { setSendQueue((n) => Math.max(0, n - 1)); }
  }

  // v4.3.1 : VÉRIFICATION TOFU À L'ENVOI. Les groupes n'en avaient AUCUNE :
  // si la clé d'un membre avait changé (compte recréé), on chiffrait avec
  // l'ancienne → message livré mais À JAMAIS indéchiffrable chez lui, sans
  // erreur nulle part. Vérifie la clé auprès de l'annuaire (cache 5 min) ;
  // clé changée = échec EXPLICITE pour ce destinataire (bannière v3.1) —
  // jamais d'acceptation automatique (TOFU intact). Annuaire injoignable :
  // on n'empêche pas l'envoi (même best-effort que openConversation).
  async function verifyContactKey(contact) {
    if (Date.now() - (keyCheckRef.current.get(contact.pseudo) || 0) < 5 * 60000) return;
    let changed = false;
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(contact.pseudo)}`);
      if (res.ok) {
        const u = await res.json();
        changed = u.publicKey !== contact.publicKey;
        if (!changed) keyCheckRef.current.set(contact.pseudo, Date.now());
      }
    } catch (_) { return; }
    if (changed) {
      throw new Error(`clé de « ${contact.pseudo} » changée (compte recréé ?) — ouvrez sa conversation 1-à-1 et validez la nouvelle clé.`);
    }
  }

  // v4.3 : RENVOYER un message échoué ou resté bloqué en « envoi » (⏳). On
  // retire l'ancienne bulle puis on repasse par le pipeline d'envoi COMPLET
  // (nouveau chiffrement, nouveaux nonces — invariant 2 ; re-résolution des
  // membres du groupe). Le contenu vit encore en mémoire dans `msg` même
  // après suppression de la ligne IndexedDB : si le renvoi échoue aussi, une
  // nouvelle bulle « échec » le matérialise — rien n'est jamais perdu.
  // Cas limite assumé : un message ⏳ que le serveur avait EN FAIT bien reçu
  // (confirmation perdue) sera dupliqué chez le destinataire — le renvoi est
  // un geste volontaire de l'utilisateur, prévenu par le libellé du bouton.
  const EXT_MIME = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif' };
  async function resendMessage(msg) {
    if (!msg || msg.from !== identity.pseudo) return;
    let fileLike = null;
    if (msg.msgType !== 'text' && msg.data) {
      const raw = Array.isArray(msg.data)
        ? await new Blob(msg.data).arrayBuffer() // vidéo par morceaux : recollée
        : msg.data;
      fileLike = {
        name: msg.fileName || 'fichier',
        type: msg.mimeType || EXT_MIME[(msg.fileName || '').split('.').pop()?.toLowerCase()] || '',
        __forceType: msg.msgType === 'audio' ? 'audio' : undefined,
        __mimeType: msg.mimeType || '',
        arrayBuffer: () => Promise.resolve(raw),
      };
    }
    await store.deleteMessage(msg.localId);
    setMessages(await store.getMessages(activePeer));
    await sendWithQueue(msg.text || '', fileLike, () => {}, { ephemeralSeconds: normalizeEphemeralSeconds(msg.ephemeralSeconds), viewOnce: msg.viewOnce === true });
  }

  // --- v4.0 : réaction emoji (enveloppe chiffrée, comme un accusé) ---
  async function reactToMessage(msg, emoji) {
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    const targets = group
      ? (group.members || []).filter((p) => p !== identity.pseudo)
      : [activePeer];
    // Application locale immédiate.
    const reactions = { ...(msg.reactions || {}) };
    reactions[emoji] = [...new Set([...(reactions[emoji] || []), identity.pseudo])];
    await store.updateMessage(msg.localId, { reactions });
    setMessages(await store.getMessages(activePeer));
    for (const p of targets) {
      try {
        const c = await ensureContact(p);
        const enc = encryptFor(makeEnvelope('reaction', {
          targetId: msg.mid || msg.serverId, emoji,
          groupId: group ? group.groupId : undefined,
        }), c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: p, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
      } catch (_) { /* membre injoignable : ignoré */ }
    }
  }

  // --- v4.1 : modifier un message déjà envoyé ---
  // Même principe qu'une réaction : une enveloppe chiffrée de plus, le
  // serveur ne voit qu'un message 1-à-1 banal. Ne s'applique qu'aux messages
  // texte (une pièce jointe déjà livrée ne peut pas être « remplacée »).
  async function editMessage(msg, newText) {
    if (!newText.trim() || newText === msg.text) return;
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    const targets = group
      ? (group.members || []).filter((p) => p !== identity.pseudo)
      : [activePeer];
    // Application locale immédiate.
    await store.updateMessage(msg.localId, { text: newText, edited: true });
    setMessages(await store.getMessages(activePeer));
    for (const p of targets) {
      try {
        const c = await ensureContact(p);
        const enc = encryptFor(makeEnvelope('edit', {
          targetId: msg.mid || msg.serverId, newText,
          groupId: group ? group.groupId : undefined,
        }), c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: p, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
      } catch (_) { /* membre injoignable : ignoré, il gardera l'ancienne version */ }
    }
  }

  // --- v4.0 : gestion de groupe avancée ---
  async function updateGroup(groupId, changes) {
    const g = await store.getGroup(groupId);
    if (!g) return;
    const next = { ...g, name: changes.name ?? g.name, members: changes.members ?? g.members ?? [] };
    await store.saveGroup(next);
    await refreshAll();
    // Propagation chiffrée à tous les membres restants.
    for (const p of next.members.filter((x) => x !== identity.pseudo)) {
      try {
        const c = await ensureContact(p);
        const enc = encryptFor(makeEnvelope('group-update', {
          groupId, name: next.name, members: next.members,
        }), c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: p, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
      } catch (_) { /* injoignable : la MAJ arrivera via un futur échange */ }
    }
    // Invitation explicite pour un membre nouvellement ajouté (pour qu'il ait le groupe).
    if (changes.added) {
      for (const p of changes.added) {
        try {
          const c = await ensureContact(p);
          const enc = encryptFor(makeEnvelope('group-invite', {
            groupId, groupName: next.name, members: next.members,
          }), c.publicKey, identity.privateKey);
          wsRef.current.send({ type: 'send', to: p, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
        } catch (_) {}
      }
    }
  }

  async function sendPoll(question, options) {
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    const targets = group ? (group.members || []).filter((p) => p !== identity.pseudo) : [activePeer];
    const mid = crypto.randomUUID();
    if (!group && activePeer === identity.pseudo) {
      await store.saveMessage({ peer: activePeer, from: identity.pseudo, msgType: 'text', text: '', sentAt: Date.now(), read: true, status: 'sent', mid, total: 1, sentCount: 1, deliveredCount: 1, failedCount: 0, serverIds: [], poll: { question, options, votes: {} } });
      setMessages(await store.getMessages(activePeer)); await refreshAll(); return;
    }
    const localId = await store.saveMessage({
      peer: activePeer, from: identity.pseudo, msgType: 'text', text: '', sentAt: Date.now(),
      read: true, status: 'sending', mid, total: targets.length || 1, sentCount: 0,
      deliveredCount: 0, failedCount: 0, serverIds: [], poll: { question, options, votes: {} },
    });
    setMessages(await store.getMessages(activePeer));
    for (const target of targets) {
      try {
        const c = await ensureContact(target);
        const tmpId = crypto.randomUUID(); idMapRef.current.byTmp.set(tmpId, localId);
        const enc = encryptFor(makeEnvelope('poll', {
          question, options, mid, groupId: group?.groupId, groupName: group?.name, members: group?.members,
        }), c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: target, msgType: 'text', tmpId, ...enc });
      } catch (_) { await store.mutateMessage(localId, (m) => ({ ...m, failedCount: (m.failedCount || 0) + 1 })); }
    }
    await refreshAll();
  }

  async function votePoll(msg, optionIndex) {
    if (!msg?.poll || !Number.isInteger(optionIndex)) return;
    const votes = { ...(msg.poll.votes || {}) };
    for (const key of Object.keys(votes)) votes[key] = (votes[key] || []).filter((p) => p !== identity.pseudo);
    votes[optionIndex] = [...new Set([...(votes[optionIndex] || []), identity.pseudo])];
    await store.updateMessage(msg.localId, { poll: { ...msg.poll, votes } });
    setMessages(await store.getMessages(activePeer));
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    const targets = group ? (group.members || []).filter((p) => p !== identity.pseudo) : [activePeer];
    for (const target of targets) {
      try {
        const c = await ensureContact(target);
        const enc = encryptFor(makeEnvelope('poll-vote', { targetId: msg.mid || msg.serverId, optionIndex, groupId: group?.groupId }), c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: target, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
      } catch (_) {}
    }
  }

  async function saveShopping(next) { setShoppingItems(next); await store.setSetting('shoppingItems', next); }
  function addShoppingItem(text) { saveShopping([...shoppingItems, { id: crypto.randomUUID(), text, checked: false }]); }
  function toggleShoppingItem(id) { saveShopping(shoppingItems.map((x) => x.id === id ? { ...x, checked: !x.checked } : x)); }
  function clearCheckedShopping() { saveShopping(shoppingItems.filter((x) => !x.checked)); }
  async function sendDirectText(to, text) {
    const c = await ensureContact(to);
    const tmpId = crypto.randomUUID(); const mid = crypto.randomUUID();
    const localId = await store.saveMessage({ peer: to, from: identity.pseudo, msgType: 'text', text, sentAt: Date.now(), read: true, status: 'sending', mid, total: 1, sentCount: 0, deliveredCount: 0, failedCount: 0, serverIds: [] });
    idMapRef.current.byTmp.set(tmpId, localId);
    const enc = encryptFor(makeEnvelope('text', { text, mid }), c.publicKey, identity.privateKey);
    wsRef.current.send({ type: 'send', to, msgType: 'text', tmpId, ...enc });
    await refreshAll();
  }
  async function shareShopping(items, destination) {
    const active = (items || []).filter((x) => x && !x.checked && typeof x.text === 'string' && x.text.trim());
    if (!active.length) { setBanner('La liste de courses est vide.'); return; }
    const chosen = String(destination || '').trim();
    if (!chosen) return;
    const group = isGroupId(chosen) ? groupOf(chosen) : null;
    if (!group && !contacts.some((c) => c.pseudo === chosen)) throw new Error('Contact ou groupe introuvable.');
    const itemTexts = normalizeShoppingTextItems(active);
    const mid = crypto.randomUUID();
    const text = `🛒 Liste de courses (${itemTexts.length} article${itemTexts.length > 1 ? 's' : ''})`;
    const targets = group ? (group.members || []).filter((pp) => pp !== identity.pseudo) : [chosen];
    if (!targets.length) throw new Error('Aucun destinataire joignable.');
    const localId = await store.saveMessage({
      peer: chosen, from: identity.pseudo, msgType: 'text', text,
      shoppingList: { title: 'Liste de courses', items: itemTexts },
      sentAt: Date.now(), read: true, status: 'sending', mid, total: targets.length,
      sentCount: 0, deliveredCount: 0, failedCount: 0, serverIds: [],
    });
    let queued = 0;
    for (const target of targets) {
      try {
        const c = await ensureContact(target);
        const tmpId = crypto.randomUUID();
        idMapRef.current.byTmp.set(tmpId, localId);
        const enc = encryptFor(makeEnvelope('shopping-list', {
          title: 'Liste de courses', items: itemTexts, mid,
          groupId: group?.groupId, groupName: group?.name, members: group?.members,
        }), c.publicKey, identity.privateKey);
        wsRef.current.send({ type: 'send', to: target, msgType: 'text', tmpId, ...enc });
        queued += 1;
      } catch (_) {}
    }
    if (!queued) { await store.updateMessage(localId, { status: 'failed' }); throw new Error('Aucun destinataire joignable.'); }
    await refreshAll();
    setBanner(`Liste envoyée à ${group ? group.name : chosen}.`); setTimeout(() => setBanner(''), 4000);
  }

  async function importShoppingList(messageItems, onlyNew = true) {
    const merged = mergeShoppingItems(shoppingItems, messageItems, onlyNew, () => crypto.randomUUID());
    if (!merged.added) { setBanner('Tous les articles sont déjà dans votre liste.'); return 0; }
    await saveShopping(merged.items);
    setBanner(`${merged.added} article${merged.added > 1 ? 's' : ''} importé${merged.added > 1 ? 's' : ''}.`);
    setTimeout(() => setBanner(''), 4000);
    return merged.added;
  }

  // --- v4.0 : export / import d'historique chiffré ---
  async function exportHistory(passphrase) {
    const hist = await store.collectHistory();
    const blob = new Blob([encryptHistory(hist, passphrase)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historique-${identity.pseudo}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    // v4.1 : export réussi → on repousse le prochain rappel de 14 jours.
    const exportedAt = Date.now();
    await store.setSetting('lastExportAt', exportedAt);
    setLastExportAt(exportedAt);
    setExportReminder(false);
  }
  async function importHistory(file, passphrase) {
    const data = decryptHistory(await file.text(), passphrase);
    await store.restoreHistory(data); // REMPLACEMENT (identité préservée)
    // Un ancien export peut contenir des messages éphémères déjà expirés :
    // ils sont supprimés localement immédiatement au lieu de réapparaître jusqu'au prochain timer.
    await store.deleteExpiredMessages(Date.now());
    setPinnedConversations(normalizeConversationKeys(await store.getSetting('pinnedConversations', [])));
    setArchivedConversations(normalizeConversationKeys(await store.getSetting('archivedConversations', [])));
    setViewOnceConversations(normalizeConversationKeys(await store.getSetting('viewOnceConversations', [])));
    const importedDeletionIds = normalizeConversationKeys(await store.getSetting('remoteDeletionIds', []));
    setRemoteDeletionIds(importedDeletionIds); remoteDeletionIdsRef.current = importedDeletionIds;
    await refreshAll();
    if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
  }

  // --- v4.0 : suppression de compte (serveur + local) ---
  function deleteAccount() {
    wsRef.current.deleteAccount(); // le handler onAccountDeleted purge local + reload
  }

  // --- "Annuler l'envoi" : suppression anticipée côté serveur ---
  function unsendMessage(msg) {
    if (!msg.serverIds?.length) return;
    for (const sid of msg.serverIds) wsRef.current.unsend(sid);
  }

  // --- Indicateur d'écriture (éphémère, throttlé côté Chat) ---
  function sendTyping() {
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    const targets = group ? (group.members || []).filter((p) => p !== identity.pseudo) : [activePeer];
    for (const t of targets) wsRef.current?.typing(t);
  }

  // --- v3.0 : sécurisation d'un compte hérité (clé aléatoire → dérivée) ---
  // Génère une passphrase, dérive une NOUVELLE paire, et demande au serveur la
  // rotation via la connexion authentifiée par l'ANCIENNE clé. Les contacts
  // verront l'alerte TOFU au prochain échange : comportement voulu, la
  // vérification d'empreinte reste la parade de référence.
  function beginSecureAccount() {
    const passphrase = generatePassphrase();
    const keys = deriveKeyPairFromPassphrase(identity.pseudo, passphrase);
    setSecuring({ passphrase, keys, confirmed: false });
  }
  function confirmSecureAccount() {
    wsRef.current.rotateKey(securing.keys.publicKey);
  }

  async function toggleNotifications() {
    try {
      if (!notifEnabled) {
        await subscribePush(wsRef.current);
        setNotifEnabled(true); await store.setSetting('notifications', true);
        setBanner('Notifications push activées sur cet appareil.');
      } else {
        await unsubscribePush(wsRef.current);
        setNotifEnabled(false); await store.setSetting('notifications', false);
        setBanner('Notifications push désactivées sur cet appareil.');
      }
      setTimeout(() => setBanner(''), 4000);
    } catch (e) {
      setBanner(e.message || 'Impossible de configurer les notifications push.');
      setTimeout(() => setBanner(''), 6000);
    }
  }
  async function toggleReceipts() {
    const v = !receiptsEnabled;
    setReceiptsEnabled(v); await store.setSetting('readReceipts', v);
  }
  async function toggleDark() {
    const v = !darkMode;
    setDarkMode(v); await store.setSetting('darkMode', v);
  }

  // v4.1 : taille de police (cycle sm→md→lg→xl) et contraste élevé.
  async function setFontScaleAndSave(v) {
    setFontScale(v); await store.setSetting('fontScale', v);
  }
  async function toggleHighContrast() {
    const v = !highContrast;
    setHighContrast(v); await store.setSetting('highContrast', v);
  }


  async function setUiStyleAndSave(value) {
    const safe = normalizeUiStyle(value);
    setUiStyle(safe); await store.setSetting('uiStyle', safe);
  }

  async function setBubbleColorAndSave(value) {
    const allowed = new Set(['blue', 'purple', 'green', 'teal', 'graphite', 'orange', 'pink']);
    const safe = allowed.has(value) ? value : 'blue';
    setBubbleColor(safe);
    await store.setSetting('bubbleColor', safe);
  }

  function requestAdminAction(action) {
    setAdminError(''); setAdminResult(null); setAdminPreview(null);
    try { wsRef.current?.adminPreview(action); }
    catch (e) { setAdminError(e.message || 'Action administrateur indisponible.'); }
  }
  function executeAdminAction(token, phrase) {
    setAdminError('');
    try { wsRef.current?.adminExecute(token, phrase); }
    catch (e) { setAdminError(e.message || 'Action administrateur indisponible.'); }
  }
  function cancelAdminAction() { setAdminPreview(null); setAdminError(''); }

  async function repairAppWithoutDataLoss() {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.filter((k) => k.startsWith('msg-static-')).map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.update().catch(() => {})));
      }
      setBanner('Caches statiques actualisés. Vos messages et votre identité sont conservés. Rechargement…');
      setTimeout(() => location.reload(), 800);
    } catch (e) {
      setBanner(`Réparation impossible : ${e.message}`);
    }
  }

  // v4.1 : contacts bloqués — purement local. Les messages entrants d'un
  // pseudo bloqué sont acquittés (pour vider la file du serveur) mais jamais
  // affichés ni stockés : zéro nouvelle métadonnée serveur, la personne
  // bloquée continue d'ignorer qu'elle l'est (pas de « lu » envoyé non plus,
  // cohérent avec l'option d'accusés déjà existante).
  async function toggleBlocked(pseudo) {
    const cur = normalizeBlocked(blocked); // v4.3.7 : null/ancienne valeur locale tolérée
    const p = typeof pseudo === 'string' ? pseudo.trim() : '';
    if (!p) return;
    const next = cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
    setBlocked(next); await store.setSetting('blockedPseudos', next);
  }

  // v4.1 : rappel d'export périodique — aucune conséquence si ignoré, juste
  // un signal pour éviter une perte d'historique silencieuse (pas de sync).
  function dismissExportReminder() { setExportReminder(false); }

  async function removeConversation(peer) {
    if (isGroupId(peer)) await store.deleteGroupConversation(peer);
    else await store.deleteConversation(peer);
    await setConversationPinned(peer, false);
    await setConversationArchived(peer, false);
    await refreshAll();
    if (activePeerRef.current === peer) setActivePeer(null);
  }
  async function removeMessage(localId) {
    await store.deleteMessage(localId);
    if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
    await refreshAll();
  }

  async function consumeViewOnce(msg) {
    if (!msg?.viewOnce) return;
    await store.deleteMessage(msg.localId);
    if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
    await refreshAll();
  }

  async function deleteForEveryone(msg) {
    if (!msg || msg.from !== identity?.pseudo || !(msg.mid || msg.serverId)) return;
    if (msg.status === 'scheduled') return unsendMessage(msg);
    if (!wsRef.current?.isConnected?.()) {
      setBanner('Suppression pour tout le monde : connexion serveur requise. Le message reste sur cet appareil pour pouvoir réessayer.');
      return;
    }
    if (!window.confirm('Supprimer ce message de votre appareil et demander sa suppression sur les appareils des destinataires ? Cette action ne peut pas effacer une capture ou un export déjà réalisé.')) return;
    const targetId = msg.mid || msg.serverId;
    const group = isGroupId(activePeer) ? groupOf(activePeer) : null;
    const targets = (group ? (group.members || []).filter((p) => p !== identity.pseudo) : [activePeer]).filter(Boolean);
    try {
      // Prépare d'abord TOUS les ordres chiffrés. Si une clé de contact manque,
      // on ne supprime pas la copie locale : l'utilisateur pourra réessayer.
      const packets = [];
      for (const p of targets) {
        const c = await ensureContact(p);
        const enc = encryptFor(makeEnvelope('delete-for-everyone', {
          targetId,
          groupId: group ? group.groupId : undefined,
        }), c.publicKey, identity.privateKey);
        packets.push({ type: 'send', to: p, msgType: 'text', tmpId: crypto.randomUUID(), silent: true, ...enc });
      }
      for (const packet of packets) {
        const sent = wsRef.current.send(packet, { queue: false });
        if (!sent) throw new Error('connexion perdue pendant la suppression');
      }
      // Une fois les ordres remis à la socket authentifiée, la copie locale peut
      // disparaître. Le serveur stocke ces ordres comme n'importe quel ciphertext
      // et les destinataires hors ligne les recevront à leur reconnexion.
      await store.deleteMessage(msg.localId);
      if (activePeerRef.current) setMessages(await store.getMessages(activePeerRef.current));
      await refreshAll();
    } catch (e) {
      setBanner(`Suppression pour tout le monde non envoyée : ${e.message || 'erreur'}. Le message local est conservé.`);
    }
  }

  if (vaultPhase === 'checking') return (
    <div className="skeleton"><div className="sk-row" /><div className="sk-row short" /><div className="sk-row" /></div>
  );
  if (vaultPhase === 'setup' || vaultPhase === 'locked') return (
    <LocalVaultScreen mode={vaultPhase} legacyRows={vaultLegacyRows} error={vaultError} biometricConfigured={!!vaultWebAuthnConfig} biometricAvailable={vaultBiometricAvailable} onSetup={setupLocalVault} onSetupAutomatic={setupAutomaticVault} onUnlock={unlockLocalVault} onBiometricUnlock={unlockLocalVaultBiometric} />
  );

  if (!ready) return (
    <div className="skeleton">
      <div className="sk-row" /><div className="sk-row short" />
      <div className="sk-row" /><div className="sk-row short" /><div className="sk-row" />
    </div>
  );
  if (!identity) {
    return <Login onDone={async (id) => {
      await store.saveIdentity(id);
      setFirstRunNotice(true);
      await startSession(id);
    }} />;
  }

  if (appLockConfigured && appLocked) {
    return <LocalLockScreen onUnlock={unlockLocalApp} />;
  }

  const legacy = !identity.secured;
  const pastDeadline = Date.now() > TRANSITION_DEADLINE;
  const daysLeft = Math.max(0, Math.ceil((TRANSITION_DEADLINE - Date.now()) / 86400000));

  // Dialogue de sécurisation (affichage unique de la passphrase, comme à la création).
  if (securing) {
    return (
      <div className="center">
        <div className="card">
          <h1>Sécuriser mon compte</h1>
          <p className="small"><strong>Votre passphrase de récupération — affichée UNE SEULE FOIS :</strong></p>
          <p className="fingerprint">{securing.passphrase}</p>
          <button type="button" className="ghost"
                  onClick={() => navigator.clipboard?.writeText(securing.passphrase)}>📋 Copier</button>
          <p className="muted small">
            Pseudo + passphrase permettront de récupérer ce compte sur n'importe
            quel appareil, même si le navigateur efface ses données. Votre clé
            publique va changer : vos contacts verront une alerte de vérification
            (normal et voulu). Vos conversations locales sont conservées.
          </p>
          <label className="row" style={{ alignItems: 'center' }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={securing.confirmed}
                   onChange={(e) => setSecuring({ ...securing, confirmed: e.target.checked })} />
            <span className="small">J'ai enregistré ma passphrase en lieu sûr</span>
          </label>
          <button disabled={!securing.confirmed || status !== 'connected'} onClick={confirmSecureAccount}>
            {status === 'connected' ? 'Activer la nouvelle clé' : 'Connexion au serveur…'}
          </button>
          <button className="ghost" onClick={() => setSecuring(null)}>Annuler</button>
        </div>
      </div>
    );
  }

  // Après l'échéance : un compte hérité DOIT être sécurisé pour continuer.
  if (legacy && pastDeadline) {
    return (
      <div className="center">
        <div className="card">
          <h1>Sécurisation requise</h1>
          <p className="small">
            La période de transition est terminée : ce compte doit être protégé
            par une passphrase de récupération pour continuer à être utilisé.
          </p>
          <button onClick={beginSecureAccount}>Sécuriser mon compte maintenant</button>
        </div>
      </div>
    );
  }

  const activeGroup = isGroupId(activePeer) ? groupOf(activePeer) : null;
  return (
    <div className="app">
      {legacy && (
        <div className="notice" onClick={beginSecureAccount} style={{ cursor: 'pointer' }}>
          🔐 Sécurisez votre compte avec une passphrase de récupération ({daysLeft} jour{daysLeft > 1 ? 's' : ''} restant{daysLeft > 1 ? 's' : ''}) —
          sinon il sera irrécupérable si le navigateur efface ses données. Cliquez ici.
        </div>
      )}
      {firstRunNotice && (
        <div className="notice" onClick={() => setFirstRunNotice(false)}>
          Vos messages sont stockés uniquement sur cet appareil. Un autre appareil
          aura un historique séparé. (cliquer pour fermer)
        </div>
      )}
      {banner && <div className="notice" onClick={() => setBanner('')}>{banner}</div>}
      {!storagePersisted && (
        <div className="notice warn">
          ⚠️ Le navigateur n'a pas accordé le stockage persistant : votre historique
          local pourrait être effacé s'il manque d'espace (Safari mobile est
          particulièrement agressif après quelques jours d'inactivité). Votre compte,
          lui, reste récupérable avec votre passphrase.
          {!isStandalonePwa() && (
            <>
              {' '}Astuce : <strong>ajoutez cette page à l'écran d'accueil</strong>
              {' '}(bouton Partager → « Sur l'écran d'accueil ») — l'app installée
              échappe au nettoyage automatique de Safari, contrairement à un onglet
              classique.
            </>
          )}
        </div>
      )}
      {!activePeer ? (
        <ConversationList
          identity={identity}
          contacts={contacts}
          groups={groups}
          convMeta={convMeta}
          status={status}
          notifEnabled={notifEnabled}
          receiptsEnabled={receiptsEnabled}
          darkMode={darkMode}
          fontScale={fontScale}
          highContrast={highContrast}
          blocked={blocked}
          exportReminder={exportReminder}
          secured={!!identity.secured}
          storagePersisted={storagePersisted}
          bubbleColor={bubbleColor}
          uiStyle={uiStyle}
          pinnedConversations={pinnedConversations}
          archivedConversations={archivedConversations}
          lastExportAt={lastExportAt}
          isAdmin={isAdmin}
          adminPreview={adminPreview}
          adminResult={adminResult}
          adminError={adminError}
          appLockConfigured={appLockConfigured}
          vaultEnabled={vaultPhase === 'unlocked'}
          vaultBiometricEnabled={!!vaultWebAuthnConfig}
          vaultBiometricAvailable={vaultBiometricAvailable}
          vaultLockPolicy={vaultLockPolicy}
          vaultAutomatic={vaultAutomatic}
          onChangeVaultCode={changeLocalVaultCode}
          onEnableVaultBiometric={enableVaultBiometric}
          onDisableVaultBiometric={disableVaultBiometric}
          onSetVaultLockPolicy={setVaultLockPolicy}
          onEnableVaultAutomatic={enableVaultAutomaticMode}
          onDisableVaultAutomatic={disableVaultAutomaticMode}
          onLockVaultNow={hardLockVault}
          onExportVaultBackup={exportFullVaultBackup}
          onImportVaultBackup={importFullVaultBackup}
          onSecure={beginSecureAccount}
          onExportHistory={exportHistory}
          onImportHistory={importHistory}
          onDeleteAccount={deleteAccount}
          onToggleNotifications={toggleNotifications}
          onToggleReceipts={toggleReceipts}
          onToggleDark={toggleDark}
          onSetFontScale={setFontScaleAndSave}
          onToggleHighContrast={toggleHighContrast}
          onSetBubbleColor={setBubbleColorAndSave}
          onSetUiStyle={setUiStyleAndSave}
          onSetConversationPinned={setConversationPinned}
          onSetConversationArchived={setConversationArchived}
          onRepairApp={repairAppWithoutDataLoss}
          onAdminPreview={requestAdminAction}
          onAdminExecute={executeAdminAction}
          onAdminCancel={cancelAdminAction}
          onConfigureAppLock={configureLocalLock}
          onDisableAppLock={disableLocalLock}
          onLockAppNow={lockLocalAppNow}
          onToggleBlocked={toggleBlocked}
          onDismissExportReminder={dismissExportReminder}
          onCreateGroup={createGroup}
          onOpen={openConversation}
          onDeleteConversation={removeConversation}
          onStartAudioCallFor={startAudioCall}
          onStartVideoCallFor={startVideoCall}
          audioCallAvailable={rtcInfo.enabled && supportsAudioCalls() && status === 'connected' && !call}
          videoCallAvailable={rtcInfo.enabled && supportsVideoCalls() && status === 'connected' && !call}
          onAddContactByQr={addOrVerifyContactByQr}
          onLogout={async () => {
            wsRef.current?.close(); clearCallHistory();
            try { localStorage.removeItem('messagerie:last-audio-network:v1'); localStorage.removeItem('messagerie:last-audio-quality:v1'); } catch (_) {}
            await store.clearIdentity(); location.reload();
          }}
        />
      ) : (
        <Chat
          identity={identity}
          peer={activePeer}
          group={activeGroup}
          contact={contacts.find((c) => c.pseudo === activePeer)}
          messages={messages}
          online={!!presence[activePeer]}
          typing={typing}
          keyAlert={keyAlert}
          blocked={blocked}
          onAcceptNewKey={acceptNewKey}
          onTyping={sendTyping}
          onUnsend={unsendMessage}
          onBack={async () => { await store.markConversationRead(activePeer); await refreshAll(); setActivePeer(null); setFocusMessageId(null); }}
          onSend={sendWithQueue}
          onResend={resendMessage}
          onDeleteMessage={removeMessage}
          onDeleteForEveryone={deleteForEveryone}
          onConsumeViewOnce={consumeViewOnce}
          onReact={reactToMessage}
          onEdit={editMessage}
          onUpdateGroup={updateGroup}
          onToggleBlocked={toggleBlocked}
          onAddContactByQr={addOrVerifyContactByQr}
          replyTo={replyTo}
          onSetReply={setReplyTo}
          sendQueue={sendQueue}
          allContacts={contacts}
          allGroups={groups}
          onSendPoll={sendPoll}
          onVotePoll={votePoll}
          shoppingItems={shoppingItems}
          onAddShoppingItem={addShoppingItem}
          onToggleShoppingItem={toggleShoppingItem}
          onClearCheckedShopping={clearCheckedShopping}
          onShareShopping={shareShopping}
          onImportShoppingList={importShoppingList}
          onStartAudioCall={startAudioCall}
          onStartVideoCall={startVideoCall}
          audioCallAvailable={rtcInfo.enabled && supportsAudioCalls() && status === 'connected' && !call}
          videoCallAvailable={rtcInfo.enabled && supportsVideoCalls() && status === 'connected' && !call}
          audioCallDisabledReason={rtcInfo.reason || (status !== 'connected' ? 'Connexion serveur requise.' : '')}
          videoCallDisabledReason={rtcInfo.reason || (status !== 'connected' ? 'Connexion serveur requise.' : '')}
          focusMessageId={focusMessageId}
          viewOnceMode={viewOnceConversations.includes(activePeer)}
          onSetViewOnceMode={(enabled) => setConversationViewOnce(activePeer, enabled)}
          autoStartVoiceToken={voiceMessageRequest?.peer === activePeer ? voiceMessageRequest.token : 0}
          onAutoStartVoiceHandled={() => setVoiceMessageRequest(null)}
        />
      )}
      <AudioCallOverlay
        call={call}
        onAccept={acceptAudioCall}
        onDecline={declineAudioCall}
        onHangup={hangupAudioCall}
        onToggleMute={toggleCallMute}
        onToggleVideo={toggleCallVideo}
        onSwitchCamera={switchCallCamera}
        onDismiss={dismissAudioCall}
        onLeaveVoiceMessage={leaveVoiceMessageAfterCall}
      />
    </div>
  );
}
